import { Fragment, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { fetchUserById } from "../../../../Profile/redux/thunks/userThunks";
import { fetchDoctorById, getCurrentDoctor } from "../../../../authDoctor/redux/thunks/doctorsThunks";
import {
  fetchConsultationById,
  fetchConsultationsByPatientId,
  updateConsultationPayment,
} from "../../../../consultation/redux/thunks/consultationThunks";
import "./PaymentTab.css";

const readAmount = (value) => Number(String(value || "").replace(/[^\d.]/g, "")) || 0;

const isVoidedPayment = (entry) =>
  ["voided", "cancelled", "canceled"].includes(
    String(entry?.status || entry?.paymentStatus || "").toLowerCase(),
  );

const getPaymentStatus = (entry) => {
  if (isVoidedPayment(entry)) return "voided";
  return String(entry?.status || entry?.paymentStatus || "completed").toLowerCase();
};

const getActorName = (actor) => {
  if (typeof actor === "string") return actor.trim();
  if (!actor || typeof actor !== "object") return "";
  const relatedActor = actor.user || actor.doctor || actor.assistant || actor.profile;
  return actor.fullName || actor.displayName || actor.name || actor.username ||
    [actor.firstName, actor.lastName].filter(Boolean).join(" ") || actor.email ||
    relatedActor?.fullName || relatedActor?.displayName || relatedActor?.name ||
    [relatedActor?.firstName, relatedActor?.lastName].filter(Boolean).join(" ") ||
    relatedActor?.email || "";
};

const getActorRole = (entry = {}) => {
  const actor = [entry.recordedBy, entry.recordedByUser, entry.actor, entry.createdByUser, entry.createdBy, entry.user]
    .find((value) => value && typeof value === "object");
  const role = entry.recordedByRole || entry.actorRole || entry.createdByRole ||
    actor?.role || actor?.userRole || actor?.type || "";
  return typeof role === "string" ? role : role?.name || role?.label || "";
};

const isActorIdentifier = (value) =>
  /^(?:\d{5,}|[0-9a-f]{24}|[0-9a-f]{8}-[0-9a-f-]{27})$/i.test(String(value || "").trim());

const getActorInitials = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const getConsultationTotal = (consultation) =>
  Number(consultation?.consultationPrice || 0) +
  (consultation?.medicalActs || []).reduce(
    (sum, act) => sum + Number(act.price || 0),
    0
  );

const getPaymentAllocationRows = (consultation, amount) => {
  let amountLeft = Math.max(0, Number(amount) || 0);
  const items = [
    ...(Number(consultation?.consultationPrice || 0) > 0
      ? [{ kind: "consultation", description: consultation.reason || consultation.consultationType || "", amount: Number(consultation.consultationPrice) }]
      : []),
    ...(consultation?.medicalActs || []).map((act) => ({
      kind: "medical-act",
      description: act.designation || act.name || act.description || "",
      code: act.code || "",
      amount: Number(act.price || 0),
    })),
  ];

  const allocations = items.flatMap((item) => {
    const allocatedAmount = Math.min(amountLeft, item.amount);
    amountLeft -= allocatedAmount;
    return allocatedAmount > 0 ? [{ ...item, amount: allocatedAmount }] : [];
  });

  return allocations.length
    ? allocations
    : [{ kind: "consultation", description: consultation?.reason || consultation?.consultationType || "", amount: Number(amount) || 0 }];
};

const getPaymentEntries = (consultation) => {
  if (Array.isArray(consultation?.paymentHistory) && consultation.paymentHistory.length) {
    return consultation.paymentHistory;
  }
  const paymentStatus = String(consultation?.paymentStatus || "").toLowerCase();
  if (["unpaid", "voided", "cancelled", "canceled"].includes(paymentStatus)) {
    return [];
  }
  const amount = readAmount(consultation?.amount);
  if (!amount && paymentStatus !== "paid") return [];
  return [{
    amount: amount || getConsultationTotal(consultation),
    method: consultation?.paymentMethod || "cash",
    paidAt: consultation?.updatedAt || consultation?.consultationDateTime || consultation?.createdAt,
  }];
};

const readLabel = (value) =>
  typeof value === "string" ? value : value?.name || value?.label || "";

const getVisitSpecialty = (visit) =>
  readLabel(
    visit.specialty ||
      visit.speciality ||
      visit.specialtyName ||
      visit.specialityName ||
      visit.doctorSpecialty ||
      visit.doctor?.specialty ||
      visit.doctor?.speciality ||
      visit.doctor?.specialtyName ||
      visit.doctor?.specialityName,
  );

const getVisitDateKey = (visit) => {
  const value = visit.consultationDateTime || visit.createdAt;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatPlanDate = (dateKey) => {
  if (!dateKey) return "—";
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : dateKey;
};

const buildPlanInstallments = (total, count, startDate, frequency, dayOfMonth) => {
  const safeCount = Math.max(1, Number(count) || 1);
  const totalMillis = Math.max(0, Math.round((Number(total) || 0) * 1000));
  const averageMillis = Math.ceil(totalMillis / safeCount);
  const roundedBaseMillis = Math.ceil(averageMillis / 5000) * 5000;
  const baseMillis = safeCount === 1
    ? totalMillis
    : Math.min(roundedBaseMillis, Math.floor(totalMillis / (safeCount - 1)));
  const start = new Date(`${startDate || formatDateKey(new Date())}T12:00:00`);

  return Array.from({ length: safeCount }, (_, index) => {
    const dueDate = new Date(start);
    if (frequency === "weekly") {
      dueDate.setDate(dueDate.getDate() + index * 7);
    } else if (index > 0) {
      const targetMonth = dueDate.getMonth() + index;
      const targetYear = dueDate.getFullYear() + Math.floor(targetMonth / 12);
      const normalizedMonth = targetMonth % 12;
      const monthLength = new Date(targetYear, normalizedMonth + 1, 0).getDate();
      dueDate.setFullYear(targetYear, normalizedMonth, Math.min(Number(dayOfMonth) || dueDate.getDate(), monthLength));
    }
    const amountMillis = index === safeCount - 1
      ? totalMillis - baseMillis * index
      : baseMillis;
    return {
      id: `${Date.now()}-${index}`,
      date: formatDateKey(dueDate),
      amount: (amountMillis / 1000).toFixed(3),
    };
  });
};

export default function PaymentTab({ consultationId, patientId, patientName = "", patientReference = "", subprofileId, onPaid, fullPage = false, role = "doctor", doctorId }) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const receiptPreviewRef = useRef(null);
  const paymentLedgerRef = useRef(null);

  const [consultationData, setConsultationData] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [patientProfile, setPatientProfile] = useState(null);
  const [doctorProfile, setDoctorProfile] = useState(null);
  const [selectedConsultationId, setSelectedConsultationId] = useState(consultationId || "");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentShortcut, setPaymentShortcut] = useState("custom");
  const [allocationMode, setAllocationMode] = useState("oldest");
  const [linkedInstallment, setLinkedInstallment] = useState(true);
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [loadingConsultations, setLoadingConsultations] = useState(false);
  const [consultationsError, setConsultationsError] = useState(false);
  const [dashboardTab, setDashboardTab] = useState("overview");
  const [visitSearch, setVisitSearch] = useState("");
  const [visitStatus, setVisitStatus] = useState("all");
  const [visitSpecialty, setVisitSpecialty] = useState("all");
  const [visitItemType, setVisitItemType] = useState("all");
  const [visitDatePreset, setVisitDatePreset] = useState("all");
  const [visitDateFrom, setVisitDateFrom] = useState("");
  const [visitDateTo, setVisitDateTo] = useState("");
  const [selectedVisitIds, setSelectedVisitIds] = useState([]);
  const [isPlanDrawerOpen, setIsPlanDrawerOpen] = useState(false);
  const [planStep, setPlanStep] = useState(1);
  const [planSelectedVisitIds, setPlanSelectedVisitIds] = useState([]);
  const [planInstallmentCount, setPlanInstallmentCount] = useState(4);
  const [planFrequency, setPlanFrequency] = useState("monthly");
  const [planScheduleType, setPlanScheduleType] = useState("monthly");
  const [planDayOfMonth, setPlanDayOfMonth] = useState(5);
  const [planDownPaymentEnabled, setPlanDownPaymentEnabled] = useState(false);
  const [planDownPaymentAmount, setPlanDownPaymentAmount] = useState("0.000");
  const [planInstallments, setPlanInstallments] = useState([]);
  const [planFirstDueDate, setPlanFirstDueDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    date.setDate(5);
    return formatDateKey(date);
  });
  const [isFuturePlanItemOpen, setIsFuturePlanItemOpen] = useState(false);
  const [futurePlanItemName, setFuturePlanItemName] = useState("");
  const [futurePlanItemAmount, setFuturePlanItemAmount] = useState("");
  const [planPatientConsent, setPlanPatientConsent] = useState(true);
  const [planSignatureCaptured, setPlanSignatureCaptured] = useState(false);
  const [planDiscountRequested, setPlanDiscountRequested] = useState(false);
  const [planDiscountReason, setPlanDiscountReason] = useState("");
  const [planSendOptions, setPlanSendOptions] = useState({ pdf: true, sms: true, whatsapp: false, email: false });
  const [createdPlans, setCreatedPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [activePaymentPlanId, setActivePaymentPlanId] = useState(null);
  const [planStatusFilter, setPlanStatusFilter] = useState("all");
  const [paymentHistorySearch, setPaymentHistorySearch] = useState("");
  const [paymentHistoryMethodFilter, setPaymentHistoryMethodFilter] = useState("all");
  const [paymentHistoryUserFilter, setPaymentHistoryUserFilter] = useState("all");
  const [paymentHistoryPlanFilter, setPaymentHistoryPlanFilter] = useState("all");
  const [paymentHistoryFrom, setPaymentHistoryFrom] = useState("");
  const [paymentHistoryTo, setPaymentHistoryTo] = useState("");
  const [expandedPaymentId, setExpandedPaymentId] = useState(null);
  const [selectedReceiptId, setSelectedReceiptId] = useState(null);
  const [receiptShareMessage, setReceiptShareMessage] = useState("");

  useEffect(() => {
    setSelectedConsultationId(consultationId || "");
  }, [consultationId]);

  useEffect(() => {
    let isCurrent = true;

    const doctorRequest = role === "assistant"
      ? doctorId
        ? dispatch(fetchDoctorById({ id: doctorId })).unwrap()
        : Promise.resolve(null)
      : dispatch(getCurrentDoctor()).unwrap();

    doctorRequest
      .then((doctor) => {
        if (isCurrent && doctor) setDoctorProfile(doctor);
      })
      .catch((error) => {
        console.error("[PaymentTab] erreur chargement médecin", error);
      });

    if (patientId) {
      dispatch(fetchUserById({
        userId: subprofileId || patientId,
        principalUserId: patientId,
        subprofileId: subprofileId || null,
      }))
        .unwrap()
        .then((patient) => {
          if (isCurrent) setPatientProfile(patient);
        })
        .catch((error) => {
          console.error("[PaymentTab] erreur chargement patient", error);
        });
    }

    return () => {
      isCurrent = false;
    };
  }, [dispatch, patientId, subprofileId, role, doctorId]);

  useEffect(() => {
    if (!patientId) return;
    setLoadingConsultations(true);
    setConsultationsError(false);
    dispatch(fetchConsultationsByPatientId({ userId: patientId, subprofileId }))
      .unwrap()
      .then(async (result) => {
        const visits = Array.isArray(result)
          ? result
          : Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result?.items)
          ? result.items
          : Array.isArray(result?.consultations)
          ? result.consultations
          : [];
        const detailedVisits = await Promise.all(visits.map((visit) =>
          visit.id
            ? dispatch(fetchConsultationById(visit.id)).unwrap().catch(() => visit)
            : visit
        ));
        setConsultations(detailedVisits);
        setSelectedConsultationId((current) => current || detailedVisits[0]?.id || "");
      })
      .catch((err) => {
        setConsultationsError(true);
        console.error("[PaymentTab] erreur fetch historique", err);
      })
      .finally(() => setLoadingConsultations(false));
  }, [dispatch, patientId, subprofileId]);

  useEffect(() => {
    if (!selectedConsultationId) {
      setConsultationData(null);
      return;
    }
    const load = async () => {
      setFetching(true);
      try {
        const result = await dispatch(fetchConsultationById(selectedConsultationId)).unwrap();
        setConsultationData(result);
        setPaymentMethod(result?.paymentMethod || "cash");
        setConsultations((previous) => {
          const next = previous.filter((item) => String(item.id) !== String(result.id));
          return [result, ...next];
        });
      } catch (err) {
        console.error("[PaymentTab] erreur fetch consultation", err);
      } finally {
        setFetching(false);
      }
    };

    load();
  }, [selectedConsultationId, dispatch]);

  const consultationPrice = Number(consultationData?.consultationPrice || 0);
  const medicalActsTotal = (consultationData?.medicalActs || []).reduce(
    (sum, act) => sum + Number(act.price || 0),
    0
  );
  const total = consultationPrice + medicalActsTotal;
  const currentEntries = getPaymentEntries(consultationData);
  const currentPaid = currentEntries
    .filter((entry) => !isVoidedPayment(entry))
    .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
  const remaining = Math.max(0, total - currentPaid);

  const displayVisits = consultations;
  const patientTotal = displayVisits.reduce((sum, visit) => sum + getConsultationTotal(visit), 0);
  const patientPaid = displayVisits.reduce(
    (sum, visit) => sum + getPaymentEntries(visit)
      .filter((entry) => !isVoidedPayment(entry))
      .reduce((paidSum, entry) => paidSum + Number(entry.amount || 0), 0),
    0
  );
  const patientRemaining = Math.max(0, patientTotal - patientPaid);
  const allPayments = displayVisits.flatMap((visit) =>
    getPaymentEntries(visit).map((entry, index) => {
      const actor = entry.recordedBy || entry.recordedByName || entry.recordedByUser ||
        entry.actor || entry.actorName || entry.createdByUser || entry.createdBy || entry.user;
      const actorName = getActorName(actor);
      const actorRole = getActorRole(entry);
      const actorId = String(
        entry.recordedById || entry.actorId || entry.createdByActorId || entry.createdById ||
        (typeof actor === "string" ? actor : actor?.id || actor?._id || actor?.userId || ""),
      ).trim();
      const doctorIds = [doctorProfile?.id, doctorProfile?.doctorId, doctorProfile?.pgId]
        .filter(Boolean).map(String);
      const currentDoctorName = doctorProfile?.fullName || doctorProfile?.name ||
        [doctorProfile?.firstName, doctorProfile?.lastName].filter(Boolean).join(" ");
      const currentAssistantName = typeof localStorage !== "undefined"
        ? localStorage.getItem("assistantName") || ""
        : "";
      const assistantId = typeof localStorage !== "undefined"
        ? localStorage.getItem("assistant_id") || localStorage.getItem("assistantId") || ""
        : "";
      const matchesDoctor = doctorIds.includes(actorId);
      const matchesAssistant = Boolean(assistantId && assistantId === actorId);
      const resolvedActorName = actorName && !isActorIdentifier(actorName)
        ? actorName
        : (actorRole.toLowerCase().includes("doctor") || matchesDoctor)
        ? currentDoctorName
        : (actorRole.toLowerCase().includes("assistant") || matchesAssistant)
        ? currentAssistantName
        : t("patientPaymentHistory.recordedByUnavailable");

      return {
        ...entry,
        id: `${visit.id}-${entry.paidAt || "legacy"}-${index}`,
        consultationId: visit.id,
        description: visit.reason || visit.consultationType || visit.type || "",
        visitDate: visit.consultationDateTime || visit.createdAt,
        receiptNo: entry.receiptNo || entry.receiptNumber || entry.reference || "",
        recordedBy: resolvedActorName,
        recordedByRole: actorRole,
        status: getPaymentStatus(entry),
        planId: entry.planId || entry.installmentPlanId || "",
        installmentNumber: entry.installmentNumber || entry.installmentIndex || null,
        allocations: Array.isArray(entry.allocations) && entry.allocations.length
          ? entry.allocations
          : getPaymentAllocationRows(visit, entry.amount),
      };
    })
  ).sort((left, right) => new Date(right.paidAt || right.visitDate) - new Date(left.paidAt || left.visitDate));
  const getVisitPayment = (visit) => {
    const visitTotal = getConsultationTotal(visit);
    const paid = getPaymentEntries(visit)
      .filter((entry) => !isVoidedPayment(entry))
      .reduce(
      (sum, entry) => sum + Number(entry.amount || 0),
      0,
    );
    return {
      total: visitTotal,
      paid,
      remaining: Math.max(0, visitTotal - paid),
      status: paid >= visitTotal && visitTotal > 0 ? "paid" : paid > 0 ? "partial" : "unpaid",
    };
  };
  const visitsForFilters = consultations;
  const availableSpecialties = [...new Set(visitsForFilters.map(getVisitSpecialty).filter(Boolean))];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let dateFromKey = "";
  let dateToKey = "";
  if (visitDatePreset === "custom") {
    dateFromKey = visitDateFrom;
    dateToKey = visitDateTo;
  } else if (visitDatePreset === "today") {
    dateFromKey = formatDateKey(today);
    dateToKey = dateFromKey;
  } else if (visitDatePreset === "last-7-days" || visitDatePreset === "last-30-days") {
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() - (visitDatePreset === "last-7-days" ? 6 : 29));
    dateFromKey = formatDateKey(startDate);
    dateToKey = formatDateKey(today);
  } else if (visitDatePreset === "this-month") {
    dateFromKey = formatDateKey(new Date(today.getFullYear(), today.getMonth(), 1));
    dateToKey = formatDateKey(today);
  }
  const dashboardVisits = visitsForFilters.filter((visit) => {
    const payment = getVisitPayment(visit);
    const search = visitSearch.trim().toLowerCase();
    const searchText = [
      visit.reason,
      visit.consultationType,
      visit.type,
      visit.id,
      ...(visit.medicalActs || []).flatMap((act) => [act.name, act.description, act.code]),
    ].filter(Boolean).join(" ").toLowerCase();
    const matchesSearch = !search || searchText.includes(search);
    const matchesStatus = visitStatus === "all" || payment.status === visitStatus;
    const matchesSpecialty =
      visitSpecialty === "all" || getVisitSpecialty(visit) === visitSpecialty;
    const hasConsultation = Number(visit.consultationPrice || 0) > 0;
    const hasMedicalActs = (visit.medicalActs || []).length > 0;
    const matchesItemType =
      visitItemType === "all" ||
      (visitItemType === "consultation" && hasConsultation) ||
      (visitItemType === "medical-act" && hasMedicalActs);
    const dateKey = getVisitDateKey(visit);
    const matchesDateFrom = !dateFromKey || (dateKey && dateKey >= dateFromKey);
    const matchesDateTo = !dateToKey || (dateKey && dateKey <= dateToKey);
    return matchesSearch && matchesStatus && matchesSpecialty && matchesItemType && matchesDateFrom && matchesDateTo;
  });

  const allVisibleSelected = dashboardVisits.length > 0 && dashboardVisits.every((visit) => selectedVisitIds.includes(String(visit.id)));
  const selectVisitForPayment = (visit) => {
    if (!visit) return;
    const visitId = String(visit.id);
    setSelectedConsultationId(visitId);
    setSelectedVisitIds((current) => current.includes(visitId) ? current : [...current, visitId]);
    setActivePaymentPlanId(null);
    setPaymentAmount(getVisitPayment(visit).remaining.toFixed(3));
    setPaymentShortcut("full");
  };
  const toggleVisitSelection = (visitId) => {
    const value = String(visitId);
    const isSelected = selectedVisitIds.includes(value);
    setSelectedVisitIds((current) => isSelected
      ? current.filter((id) => id !== value)
      : [...current, value]
    );
    if (!isSelected) {
      selectVisitForPayment(dashboardVisits.find((visit) => String(visit.id) === value));
    }
  };
  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedVisitIds((current) => current.filter((id) => !dashboardVisits.some((visit) => String(visit.id) === id)));
      return;
    }
    setSelectedVisitIds((current) => {
      const next = [...current];
      dashboardVisits.forEach((visit) => {
        const value = String(visit.id);
        if (!next.includes(value)) next.push(value);
      });
      return next;
    });
    selectVisitForPayment(dashboardVisits[0]);
  };
  const nextDueVisit = consultations
    .filter((visit) => getVisitPayment(visit).remaining > 0 && (visit.dueDate || visit.nextDueDate))
    .sort((left, right) => new Date(left.dueDate || left.nextDueDate) - new Date(right.dueDate || right.nextDueDate))[0];

  const handleAddPayment = async (event) => {
    event.preventDefault();
    const paymentConsultation = fullPage
      ? consultations.find((visit) => String(visit.id) === String(selectedConsultationId))
      : consultationData;
    if (!paymentConsultation?.id) return;

    const paymentTotal = fullPage ? getConsultationTotal(paymentConsultation) : total;
    const paymentEntries = getPaymentEntries(paymentConsultation);
    const paidBeforePayment = paymentEntries
      .filter((entry) => !isVoidedPayment(entry))
      .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
    const paymentRemaining = Math.max(0, paymentTotal - paidBeforePayment);
    if (!paymentRemaining) return;

    const requestedAmount = fullPage && paymentShortcut === "full"
      ? paymentRemaining
      : fullPage && paymentShortcut === "half"
      ? Math.min(paymentTotal / 2, paymentRemaining)
      : Number(paymentAmount || (fullPage ? paymentRemaining : 0)) || 0;
    const amount = Math.min(requestedAmount, paymentRemaining);
    if (amount <= 0) return;

    setLoading(true);
    try {
      const paymentDate = new Date();
      const receiptNo = paymentReference.trim() || `REC-${formatDateKey(paymentDate).replaceAll("-", "")}-${String(Date.now()).slice(-4)}`;
      const sessionRole = String(
        role ||
          window.sessionStorage.getItem("activeSessionRole") ||
          localStorage.getItem("userRole") ||
          "doctor",
      ).toLowerCase();
      const recordedByRole = sessionRole.includes("assistant") ? "assistant" : "doctor";
      const recordedBy = recordedByRole === "assistant"
        ? localStorage.getItem("assistantName") || ""
        : doctorProfile?.fullName || doctorProfile?.name || "";
      const recordedById = recordedByRole === "assistant"
        ? localStorage.getItem("assistant_id") || localStorage.getItem("assistantId") || ""
        : doctorProfile?.id || doctorProfile?.doctorId || doctorProfile?.pgId || "";
      const paymentHistory = [
        ...paymentEntries,
        {
          amount,
          method: paymentMethod,
          paidAt: paymentDate.toISOString(),
          recordedBy,
          recordedByRole,
          recordedById,
          receiptNo,
          referenceNo: paymentReference.trim(),
          internalNote: paymentNote.trim(),
          allocations: getPaymentAllocationRows(paymentConsultation, amount),
          ...(activePaymentPlanId ? {
            planId: activePaymentPlanId,
            installmentNumber: createdPlans.find((plan) => plan.id === activePaymentPlanId)?.installments?.findIndex((installment) => !installment.paidAt) + 1,
          } : {}),
        },
      ];
      const newPaidTotal = paidBeforePayment + amount;
      const isPaid = newPaidTotal >= paymentTotal - 0.001;
      await dispatch(
        updateConsultationPayment({
          consultationId: paymentConsultation.id,
          consultationData: {
            paymentStatus: isPaid ? "paid" : "partial",
            amount: String(newPaidTotal),
            paymentMethod,
            paymentType: isPaid ? "full" : "installment",
            paymentHistory,
          },
        })
      ).unwrap();

      setConsultationData((previous) => previous && String(previous.id) === String(paymentConsultation.id)
        ? {
            ...previous,
            paymentStatus: isPaid ? "paid" : "partial",
            amount: String(newPaidTotal),
            paymentMethod,
            paymentType: isPaid ? "full" : "installment",
            paymentHistory,
          }
        : previous
      );
      setConsultations((previous) => previous.map((visit) =>
        String(visit.id) === String(paymentConsultation.id)
          ? { ...visit, paymentStatus: isPaid ? "paid" : "partial", amount: String(newPaidTotal), paymentMethod, paymentHistory }
          : visit
      ));
      if (activePaymentPlanId) {
        setCreatedPlans((previous) => previous.map((plan) => {
          if (plan.id !== activePaymentPlanId) return plan;
          let paymentToAllocate = amount;
          const installments = (plan.installments || []).map((installment) => {
            if (installment.paidAt || paymentToAllocate <= 0) return installment;
            const due = Number(installment.amount || 0);
            if (paymentToAllocate < due) {
              const remainingAmount = due - paymentToAllocate;
              paymentToAllocate = 0;
              return { ...installment, amount: remainingAmount.toFixed(3) };
            }
            paymentToAllocate -= due;
            return { ...installment, paidAt: new Date().toISOString() };
          });
          return { ...plan, paidAmount: Math.min(plan.amount, Number(plan.paidAmount || 0) + amount), installments };
        }));
        setActivePaymentPlanId(null);
      }
      setPaymentAmount("");
      setPaymentReference("");
      setPaymentNote("");
      onPaid?.();
    } catch (err) {
      console.error("[PaymentTab] erreur mise à jour paiement", err);
    } finally {
      setLoading(false);
    }
  };

  if (fullPage) {
    const selectedVisit = selectedVisitIds.includes(String(selectedConsultationId))
      ? displayVisits.find(
          (visit) => String(visit.id) === String(selectedConsultationId),
        )
      : null;
    const selectedVisitPayment = selectedVisit ? getVisitPayment(selectedVisit) : null;
    const selectedCount = dashboardVisits.filter((visit) => selectedVisitIds.includes(String(visit.id))).length;
    const localTodayKey = formatDateKey(new Date());
    const getActorRoleLabel = (role) => {
      const normalizedRole = String(role || "").toLowerCase();
      if (normalizedRole.includes("assistant") || normalizedRole.includes("secretary")) {
        return t("patientPaymentHistory.assistantRole");
      }
      if (normalizedRole.includes("doctor") || normalizedRole.includes("medecin")) {
        return t("patientPaymentHistory.doctorRole");
      }
      return role ? t("patientPaymentHistory.otherRole") : t("patientPaymentHistory.roleUnknown");
    };
    const paymentsToday = allPayments.filter((entry) => !isVoidedPayment(entry) && getVisitDateKey({ consultationDateTime: entry.paidAt || entry.visitDate }) === localTodayKey);
    const paymentMethodTotals = Object.fromEntries(
      ["cash", "card", "cheque"].map((method) => [
        method,
        paymentsToday.filter((entry) => entry.method === method).reduce((sum, entry) => sum + Number(entry.amount || 0), 0),
      ]),
    );
    const paymentHistoryUsers = [...new Set([
      ...allPayments.map((entry) => entry.recordedBy),
      doctorProfile?.fullName,
      doctorProfile?.name,
      [doctorProfile?.firstName, doctorProfile?.lastName].filter(Boolean).join(" "),
      typeof window !== "undefined" ? window.localStorage.getItem("assistantName") : "",
    ].map((name) => String(name || "").trim()).filter(Boolean))];
    const filteredPayments = allPayments.filter((entry) => {
      const query = paymentHistorySearch.trim().toLowerCase();
      const text = [
        entry.description,
        entry.receiptNo,
        entry.method,
        entry.recordedBy,
        patientName,
        patientProfile?.fullName,
        patientProfile?.name,
        patientProfile?.identifier,
        patientProfile?.patientId,
        ...(entry.allocations || []).map((allocation) => allocation.description || allocation.code),
        entry.id,
      ].filter(Boolean).join(" ").toLowerCase();
      const dateKey = getVisitDateKey({ consultationDateTime: entry.paidAt || entry.visitDate });
      return (!query || text.includes(query)) &&
        (paymentHistoryMethodFilter === "all" || entry.method === paymentHistoryMethodFilter) &&
        (paymentHistoryUserFilter === "all" || entry.recordedBy === paymentHistoryUserFilter) &&
        (paymentHistoryPlanFilter === "all" || String(entry.planId) === paymentHistoryPlanFilter) &&
        (!paymentHistoryFrom || dateKey >= paymentHistoryFrom) &&
        (!paymentHistoryTo || dateKey <= paymentHistoryTo);
    });
    const paymentMonthGroups = filteredPayments.reduce((groups, entry) => {
      const dateKey = getVisitDateKey({ consultationDateTime: entry.paidAt || entry.visitDate });
      const monthKey = dateKey.slice(0, 7) || "undated";
      groups[monthKey] ||= [];
      groups[monthKey].push(entry);
      return groups;
    }, {});
    const todayDateKey = formatDateKey(new Date());
    const getSessionPlanStatus = (plan) => {
      if (plan.status === "paused") return "paused";
      if (Number(plan.paidAmount || 0) >= Number(plan.amount || 0) && Number(plan.amount || 0) > 0) return "completed";
      const nextDue = plan.installments?.find((installment) => !installment.paidAt);
      return nextDue?.date && nextDue.date < todayDateKey ? "overdue" : "active";
    };
    const visiblePlans = createdPlans.filter((plan) => {
      const planStatus = getSessionPlanStatus(plan);
      return planStatusFilter === "all" || planStatus === planStatusFilter;
    });
    const selectedPlan = visiblePlans.find((plan) => plan.id === selectedPlanId) || visiblePlans[0] || null;
    const selectedPlanPaid = Number(selectedPlan?.paidAmount || 0);
    const selectedPlanRemaining = Math.max(0, Number(selectedPlan?.amount || 0) - selectedPlanPaid);
    const selectedPlanProgress = selectedPlan?.amount ? Math.min(100, (selectedPlanPaid / selectedPlan.amount) * 100) : 0;
    const receiptDocuments = [
      ...allPayments.map((entry) => ({ id: `receipt-${entry.id}`, type: "receipt", entry, amount: Number(entry.amount || 0) })),
      ...createdPlans.map((plan) => ({ id: `plan-${plan.id}`, type: "plan", plan, amount: Number(plan.amount || 0) })),
      { id: "statement-current", type: "statement", amount: patientRemaining },
    ];
    const selectedReceipt = receiptDocuments.find((document) => document.id === selectedReceiptId) || receiptDocuments[0];
    const receiptVisit = selectedReceipt?.type === "receipt"
      ? displayVisits.find((visit) => String(visit.id) === String(selectedReceipt.entry.consultationId))
      : null;
    const receiptEntry = selectedReceipt?.type === "receipt" ? selectedReceipt.entry : null;
    const patientDisplayName = patientProfile?.fullName || patientProfile?.name ||
      [patientProfile?.firstName, patientProfile?.lastName].filter(Boolean).join(" ") || patientName;
    const patientDisplayReference = patientProfile?.identifier || patientProfile?.patientId || patientReference || patientId;
    const doctorDisplayName = doctorProfile?.fullName || doctorProfile?.name ||
      [doctorProfile?.firstName, doctorProfile?.lastName].filter(Boolean).join(" ") || "—";
    const receiptAllocations = receiptEntry?.allocations?.length
      ? receiptEntry.allocations
      : receiptVisit
      ? getPaymentAllocationRows(receiptVisit, receiptEntry?.amount)
      : [];
    const planEligibleVisits = visitsForFilters.filter((visit) => getVisitPayment(visit).remaining > 0);
    const planSelectedVisits = planEligibleVisits.filter((visit) => planSelectedVisitIds.includes(String(visit.id)));
    const planSelectedAmount = planSelectedVisits.reduce(
      (sum, visit) => sum + getVisitPayment(visit).remaining,
      Number(futurePlanItemAmount) || 0,
    );
    const planDownPayment = planDownPaymentEnabled ? Number(planDownPaymentAmount) || 0 : 0;
    const planSchedulePrincipal = Math.max(0, planSelectedAmount - planDownPayment);
    const planScheduleRows = planInstallments.length
      ? planInstallments
      : buildPlanInstallments(planSchedulePrincipal, planInstallmentCount, planFirstDueDate, planFrequency, planDayOfMonth);
    const planScheduleTotal = planScheduleRows.reduce((sum, installment) => sum + (Number(installment.amount) || 0), 0);
    const planTotalScheduled = planScheduleTotal + planDownPayment;
    const planScheduleBalanced = Math.abs(planSelectedAmount - planTotalScheduled) < 0.001;
    const dueSoonDate = new Date();
    dueSoonDate.setDate(dueSoonDate.getDate() + 7);
    const dueSoonDateKey = formatDateKey(dueSoonDate);
    const planCoveredAmount = planSelectedVisits.reduce((sum, visit) => sum + readAmount(
      visit.cnamCoveredAmount || visit.cnssCoveredAmount || visit.insuranceCoveredAmount || visit.coveredAmount,
    ), 0);
    const eligibleSelectedVisitIds = dashboardVisits
      .filter((visit) => getVisitPayment(visit).remaining > 0 && selectedVisitIds.includes(String(visit.id)))
      .map((visit) => String(visit.id));
    const quickAmount = !selectedVisitPayment
      ? "0.000"
      : paymentShortcut === "full"
      ? selectedVisitPayment.remaining.toFixed(3)
      : paymentShortcut === "half"
      ? Math.min(selectedVisitPayment.total / 2, selectedVisitPayment.remaining).toFixed(3)
      : paymentAmount === ""
      ? selectedVisitPayment.remaining.toFixed(3)
      : paymentAmount;
    const quickStatus = !selectedVisitPayment
      ? "unpaid"
      : selectedVisitPayment.remaining <= 0
      ? "paid"
      : selectedVisitPayment.remaining < selectedVisitPayment.total
      ? "partial"
      : "unpaid";
    const dashboardTabs = [
      ["overview", "overview"],
      ["plans", "installmentPlans"],
      ["history", "paymentHistory"],
      ["receipts", "receiptsDocuments"],
    ];
    const summary = {
      totalConsultations: patientTotal,
      totalPaid: patientPaid,
      remainingBalance: patientRemaining,
      nextDue: nextDueVisit ? getVisitPayment(nextDueVisit).remaining : 0,
    };
    const openPlanDrawer = () => {
      const selectedTotal = dashboardVisits
        .filter((visit) => eligibleSelectedVisitIds.includes(String(visit.id)))
        .reduce((sum, visit) => sum + getVisitPayment(visit).remaining, 0);
      setPlanSelectedVisitIds(eligibleSelectedVisitIds);
      setPlanStep(1);
      setIsFuturePlanItemOpen(false);
      setFuturePlanItemName("");
      setFuturePlanItemAmount("");
      setPlanScheduleType("monthly");
      setPlanInstallmentCount(4);
      setPlanFrequency("monthly");
      setPlanDayOfMonth(5);
      setPlanDownPaymentEnabled(false);
      setPlanDownPaymentAmount("0.000");
      setPlanInstallments(buildPlanInstallments(selectedTotal, 4, planFirstDueDate, "monthly", 5));
      setPlanPatientConsent(true);
      setPlanSignatureCaptured(false);
      setIsPlanDrawerOpen(true);
    };
    const generatePlanSchedule = (total, count, startDate = planFirstDueDate, frequency = planFrequency, day = planDayOfMonth) => {
      setPlanInstallments(buildPlanInstallments(total, count, startDate, frequency, day));
    };
    const createPlanFromDrawer = () => {
      const planId = Date.now();
      const plan = {
        id: planId,
        number: `PLN-${String(planId).slice(-5)}`,
        status: "active",
        paidAmount: 0,
        createdAt: new Date().toISOString(),
        amount: planSelectedAmount,
        itemCount: planSelectedVisits.length + (Number(futurePlanItemAmount) > 0 ? 1 : 0),
        installmentCount: planScheduleRows.length,
        frequency: planFrequency,
        firstDueDate: planScheduleRows[0]?.date || planFirstDueDate,
        downPayment: planDownPayment,
        scheduleType: planScheduleType,
        patientConsent: planPatientConsent,
        signatureCaptured: planSignatureCaptured,
        sendOptions: planSendOptions,
        installments: planScheduleRows,
        items: [
          ...planSelectedVisits.map((visit) => visit.reason || visit.consultationType || t("consultationPage.payment.consultation")),
          ...(futurePlanItemName && Number(futurePlanItemAmount) > 0 ? [futurePlanItemName] : []),
        ],
        itemDetails: [
          ...planSelectedVisits.map((visit) => ({
            description: visit.reason || visit.consultationType || t("consultationPage.payment.consultation"),
            amount: getVisitPayment(visit).remaining,
          })),
          ...(futurePlanItemName && Number(futurePlanItemAmount) > 0
            ? [{ description: futurePlanItemName, amount: Number(futurePlanItemAmount) }]
            : []),
        ],
        visitIds: planSelectedVisits.map((visit) => String(visit.id)),
      };
      setCreatedPlans((current) => [plan, ...current]);
      setSelectedPlanId(planId);
      setIsPlanDrawerOpen(false);
      setDashboardTab("plans");
    };
    const togglePlanStatus = (plan) => {
      const status = plan.status === "paused" ? "active" : "paused";
      setCreatedPlans((current) => current.map((item) => item.id === plan.id ? { ...item, status } : item));
    };
    const requestPlanEdit = (plan) => {
      setCreatedPlans((current) => current.map((item) => item.id === plan.id ? { ...item, editRequested: true } : item));
    };
    const payNextPlanInstallment = (plan) => {
      const visit = displayVisits.find((item) => String(item.id) === String(plan.visitIds?.[0]));
      if (!visit) return;
      selectVisitForPayment(visit);
      const nextInstallment = plan.installments?.find((installment) => !installment.paidAt);
      if (nextInstallment) setPaymentAmount(String(nextInstallment.amount));
      setActivePaymentPlanId(plan.id);
      setDashboardTab("overview");
    };
    const exportPaymentHistoryCsv = () => {
      const quote = (value) => `"${String(value ?? "").replaceAll('"', '""')}"`;
      const rows = [
        ["Date", "Receipt", "Item", "Method", "Amount", "Status"],
        ...filteredPayments.map((entry) => [
          entry.paidAt || entry.visitDate || "",
          entry.receiptNo,
          entry.description,
          t(`patientPaymentHistory.paymentMethods.${entry.method || "cash"}`, { defaultValue: entry.method || "" }),
          Number(entry.amount || 0).toFixed(3),
          t(`patientPaymentHistory.${entry.status || "completed"}`),
        ]),
      ];
      const csv = `\uFEFF${rows.map((row) => row.map(quote).join(",")).join("\r\n")}`;
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "medilink-payment-history.csv";
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    };
    const renderPaymentPdf = async (element, orientation = "portrait") => {
      if (!element?.isConnected || element.getBoundingClientRect().height === 0) {
        throw new Error("Payment document is not visible");
      }

      await document.fonts?.ready;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollX: 0,
        scrollY: 0,
        windowWidth: Math.max(document.documentElement.clientWidth, element.scrollWidth),
      });
      if (!canvas.width || !canvas.height) {
        throw new Error("Payment document is empty");
      }

      const pdf = new jsPDF({ orientation, unit: "mm", format: "a4" });
      const margin = 8;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const contentWidth = pageWidth - margin * 2;
      const contentHeight = pageHeight - margin * 2;
      const imageHeight = (canvas.height * contentWidth) / canvas.width;
      const pageCount = Math.max(1, Math.ceil(imageHeight / contentHeight));
      const imageData = canvas.toDataURL("image/png");

      for (let page = 0; page < pageCount; page += 1) {
        if (page > 0) pdf.addPage();
        pdf.addImage(imageData, "PNG", margin, margin - page * contentHeight, contentWidth, imageHeight);
      }

      return { pdf, canvas };
    };

    const reportPdfError = (error) => {
      console.error("[PaymentTab] erreur génération document", error);
      setReceiptShareMessage(t("patientPaymentHistory.pdfGenerationFailed"));
    };

    const downloadPaymentHistoryPdf = async () => {
      try {
        const { pdf } = await renderPaymentPdf(paymentLedgerRef.current, "landscape");
        pdf.save(`medilink-payment-history-${formatDateKey(new Date())}.pdf`);
      } catch (error) {
        reportPdfError(error);
      }
    };

    const downloadReceiptPdf = async () => {
      try {
        const { pdf } = await renderPaymentPdf(receiptPreviewRef.current);
        pdf.save("medilink-receipt.pdf");
      } catch (error) {
        reportPdfError(error);
      }
    };

    const printReceipt = async (entry) => {
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        setReceiptShareMessage(t("patientPaymentHistory.printPopupBlocked"));
        return;
      }

      printWindow.document.write(`<!doctype html><html><head><title>${t("patientPaymentHistory.receipt")}</title><style>@page{size:A4 portrait;margin:10mm}html,body{margin:0;background:#fff}body{display:flex;justify-content:center}img{display:block;width:100%;height:auto;object-fit:contain}</style></head><body></body></html>`);
      printWindow.document.close();
      setSelectedReceiptId(`receipt-${entry.id}`);
      setDashboardTab("receipts");

      try {
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        const { canvas } = await renderPaymentPdf(receiptPreviewRef.current);
        const image = printWindow.document.createElement("img");
        image.alt = t("patientPaymentHistory.receipt");
        image.onload = () => {
          printWindow.focus();
          printWindow.print();
        };
        image.onerror = () => printWindow.close();
        image.src = canvas.toDataURL("image/png");
        printWindow.document.body.replaceChildren(image);
      } catch (error) {
        printWindow.close();
        reportPdfError(error);
      }
    };
    const sharePaymentDocument = async () => {
      const title = selectedReceipt?.type === "plan"
        ? t("patientPaymentHistory.planAgreement")
        : selectedReceipt?.type === "statement"
        ? t("patientPaymentHistory.accountStatement")
        : t("patientPaymentHistory.receipt");
      const text = `${title} · ${patientName || t("patientPaymentHistory.patient")} · ${Number(selectedReceipt?.amount || 0).toFixed(3)} TND`;
      try {
        if (navigator.share) await navigator.share({ title, text });
        else if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
        setReceiptShareMessage(t("patientPaymentHistory.shareCopied"));
      } catch (error) {
        if (error?.name !== "AbortError") setReceiptShareMessage(t("patientPaymentHistory.shareUnavailable"));
      }
    };

    return (
      <div className="payment-dashboard">
        <div className="payment-dashboard-summary">
          <article className="payment-dashboard-metric">
            <span>{t("patientPaymentHistory.totalConsultations")}</span>
            <strong>{summary.totalConsultations.toFixed(3)} TND</strong>
            <small>{t("patientPaymentHistory.allBilledItems")}</small>
          </article>
          <article className="payment-dashboard-metric is-paid">
            <span>{t("patientPaymentHistory.totalPaid")}</span>
            <strong>{summary.totalPaid.toFixed(3)} TND</strong>
            <small>{t("patientPaymentHistory.collectedPercent", { percent: patientTotal ? ((summary.totalPaid / patientTotal) * 100).toFixed(1) : "0" })}</small>
          </article>
          <article className="payment-dashboard-metric is-remaining">
            <span>{t("patientPaymentHistory.remainingBalance")}</span>
            <strong>{summary.remainingBalance.toFixed(3)} TND</strong>
            <small>{t("patientPaymentHistory.patientShare")}</small>
          </article>
          <article className="payment-dashboard-metric is-due">
            <span>{t("patientPaymentHistory.nextDue")}</span>
            <strong>{summary.nextDue.toFixed(3)} TND</strong>
            <small>{nextDueVisit?.dueDate || nextDueVisit?.nextDueDate
              ? new Date(nextDueVisit.dueDate || nextDueVisit.nextDueDate).toLocaleDateString(i18n.resolvedLanguage || i18n.language, { dateStyle: "medium" })
              : t("patientPaymentHistory.noScheduledDue")}</small>
          </article>
        </div>

        <div className="payment-dashboard-credit-row" aria-live="polite">
          <div className="payment-dashboard-credit-left">
            <span className="payment-dashboard-credit-label">{t("patientPaymentHistory.patientCredit")}:</span>
            <strong className="payment-dashboard-credit-value">0.000 TND</strong>
            <span className="payment-dashboard-credit-note">{t("patientPaymentHistory.advanceCreditNote")}</span>
          </div>
          <span className="payment-dashboard-overdue-pill">
            {t("patientPaymentHistory.overdue")}
          </span>
        </div>

        <div className="payment-dashboard-tabs" role="tablist">
          {dashboardTabs.map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={dashboardTab === key}
              className={dashboardTab === key ? "is-active" : ""}
              onClick={() => setDashboardTab(key)}
            >
              {t(`patientPaymentHistory.tabs.${label}`)}
              {key === "plans" && <span>{createdPlans.length}</span>}
              {key === "history" && <span>{allPayments.length}</span>}
            </button>
          ))}
        </div>

        {dashboardTab === "overview" && (
          <div className="payment-dashboard-layout">
            <section className="payment-dashboard-outstanding">
              <div className="payment-dashboard-header-row">
                <div className="payment-dashboard-header-main">
                  <h2>{t("patientPaymentHistory.outstandingItems")}</h2>
                  <p>{t("patientPaymentHistory.outstandingSubtitle")}</p>
                </div>
                <div className="payment-dashboard-header-actions">
                  <span className="payment-dashboard-selection-text">{t(selectedCount === 1 ? "patientPaymentHistory.selectedItem" : "patientPaymentHistory.selectedItems", { count: selectedCount })}</span>
                  <button type="button" className="payment-dashboard-action-btn secondary">{t("patientPaymentHistory.paySelected")}</button>
                  <button type="button" className="payment-dashboard-action-btn primary" onClick={openPlanDrawer} disabled={eligibleSelectedVisitIds.length === 0}>{t("patientPaymentHistory.createPlanSelected")}</button>
                </div>
              </div>
              <div className="payment-dashboard-filters">
                <div className="payment-dashboard-search-input-wrap">
                  <button type="button" className="payment-dashboard-search-button" aria-label={t("patientPaymentHistory.searchItems")}>
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10.5 18a7.5 7.5 0 1 1 5.4-12.9A7.5 7.5 0 0 1 10.5 18Zm0-2a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11Zm10.5 5.5-6.1-6.1 1.4-1.4 6.1 6.1-1.4 1.4Z"/></svg>
                  </button>
                  <input
                    value={visitSearch}
                    onChange={(event) => setVisitSearch(event.target.value)}
                    placeholder={t("patientPaymentHistory.searchItems")}
                    aria-label={t("patientPaymentHistory.searchItems")}
                  />
                </div>
                <div className="payment-dashboard-mini-filters">
                  <select
                    className="payment-dashboard-mini-filter"
                    value={visitStatus}
                    onChange={(event) => setVisitStatus(event.target.value)}
                    aria-label={t("patientPaymentHistory.filterStatus")}
                  >
                    <option value="all">{t("patientPaymentHistory.status.allStatuses")}</option>
                    <option value="paid">{t("patientPaymentHistory.status.paid")}</option>
                    <option value="partial">{t("patientPaymentHistory.status.partial")}</option>
                    <option value="unpaid">{t("patientPaymentHistory.status.unpaid")}</option>
                  </select>
                  <select
                    className="payment-dashboard-mini-filter"
                    value={visitSpecialty}
                    onChange={(event) => setVisitSpecialty(event.target.value)}
                    aria-label={t("patientPaymentHistory.filterSpecialty")}
                  >
                    <option value="all">{t("patientPaymentHistory.allSpecialties")}</option>
                    {availableSpecialties.map((specialty) => (
                      <option key={specialty} value={specialty}>{specialty}</option>
                    ))}
                  </select>
                  <select
                    className="payment-dashboard-mini-filter"
                    value={visitItemType}
                    onChange={(event) => setVisitItemType(event.target.value)}
                    aria-label={t("patientPaymentHistory.filterItemType")}
                  >
                    <option value="all">{t("patientPaymentHistory.allItemTypes")}</option>
                    <option value="consultation">{t("patientPaymentHistory.consultations")}</option>
                    <option value="medical-act">{t("patientPaymentHistory.medicalActs")}</option>
                  </select>
                  <select
                    className="payment-dashboard-mini-filter"
                    value={visitDatePreset}
                    onChange={(event) => setVisitDatePreset(event.target.value)}
                    aria-label={t("patientPaymentHistory.filterDate")}
                  >
                    <option value="all">{t("patientPaymentHistory.anyDate")}</option>
                    <option value="today">{t("patientPaymentHistory.today")}</option>
                    <option value="last-7-days">{t("patientPaymentHistory.last7Days")}</option>
                    <option value="last-30-days">{t("patientPaymentHistory.last30Days")}</option>
                    <option value="this-month">{t("patientPaymentHistory.thisMonth")}</option>
                    <option value="custom">{t("patientPaymentHistory.customRange")}</option>
                  </select>
                  {visitDatePreset === "custom" && (
                    <div className="payment-dashboard-date-range">
                      <input
                        type="date"
                        value={visitDateFrom}
                        max={visitDateTo || undefined}
                        onChange={(event) => setVisitDateFrom(event.target.value)}
                        aria-label={t("patientPaymentHistory.startDate")}
                      />
                      <span aria-hidden="true">{t("patientPaymentHistory.dateRangeTo")}</span>
                      <input
                        type="date"
                        value={visitDateTo}
                        min={visitDateFrom || undefined}
                        onChange={(event) => setVisitDateTo(event.target.value)}
                        aria-label={t("patientPaymentHistory.endDate")}
                      />
                    </div>
                  )}
                </div>
              </div>
              {loadingConsultations ? (
                <p className="payment-dashboard-empty">{t("consultationPage.payment.loadingVisits")}</p>
              ) : consultationsError ? (
                <p className="payment-dashboard-empty" role="alert">{t("consultationPage.payment.consultationsError")}</p>
              ) : dashboardVisits.length === 0 ? (
                <p className="payment-dashboard-empty">{t("patientPaymentHistory.noOutstandingItems")}</p>
              ) : (
                <>
                  <div className="payment-dashboard-table-wrap">
                    <table className="payment-dashboard-table">
                      <thead>
                        <tr>
                          <th className="payment-dashboard-select-col">
                            <input
                              type="checkbox"
                              checked={allVisibleSelected}
                              onChange={toggleSelectAllVisible}
                              aria-label={t("patientPaymentHistory.selectAllVisible")}
                            />
                          </th>
                          <th>{t("patientPaymentHistory.date")}</th>
                          <th>{t("patientPaymentHistory.description")}</th>
                          <th>{t("patientPaymentHistory.total")}</th>
                          <th>{t("patientPaymentHistory.paid")}</th>
                          <th>{t("patientPaymentHistory.remaining")}</th>
                          <th>{t("patientPaymentHistory.status.label")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardVisits.map((visit) => {
                          const payment = getVisitPayment(visit);
                          const rowSelected = selectedVisitIds.includes(String(visit.id));
                          const selected = String(visit.id) === String(selectedVisit?.id || "");
                          const descriptionTitle = visit.reason || visit.consultationType || visit.type || t("consultationPage.payment.consultation");
                          const actNames = (visit.medicalActs || [])
                            .map((act) => act.designation || act.name || act.description)
                            .filter((name) => name && name.toLowerCase() !== String(descriptionTitle).toLowerCase());
                          const actCodes = (visit.medicalActs || [])
                            .map((act) => act.code)
                            .filter(Boolean);
                          const descriptionDetails = [
                            Number(visit.consultationPrice || 0) > 0 ? t("patientPaymentHistory.consultationFee") : "",
                            actNames.length ? actNames.join(" + ") : (visit.medicalActs || []).length ? t("patientPaymentHistory.act") : "",
                            actCodes.join(", "),
                            getVisitSpecialty(visit),
                          ].filter(Boolean);
                          return (
                            <tr
                              key={visit.id}
                              className={selected ? "is-selected" : ""}
                              onClick={() => selectVisitForPayment(visit)}
                            >
                              <td className="payment-dashboard-select-col" onClick={(event) => event.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={rowSelected}
                                  onChange={() => toggleVisitSelection(visit.id)}
                                  aria-label={t("patientPaymentHistory.selectVisitLabel", { visit: visit.reason || visit.consultationType || t("consultationPage.payment.consultation") })}
                                />
                              </td>
                              <td>{new Date(visit.consultationDateTime || visit.createdAt).toLocaleDateString(i18n.resolvedLanguage || i18n.language)}</td>
                              <td className="payment-dashboard-description-cell">
                                <div className="payment-dashboard-description">
                                  <strong>{descriptionTitle}</strong>
                                  {descriptionDetails.length > 0 && <span>{descriptionDetails.join(" · ")}</span>}
                                </div>
                              </td>
                              <td>{payment.total.toFixed(3)} TND</td>
                              <td>{payment.paid.toFixed(3)} TND</td>
                              <td><strong>{payment.remaining.toFixed(3)} TND</strong></td>
                              <td><span className={`payment-dashboard-status is-${payment.status}`}>{t(`patientPaymentHistory.status.${payment.status}`)}</span></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="payment-dashboard-suggestion">
                    <div className="payment-dashboard-suggestion-icon">i</div>
                    <div className="payment-dashboard-suggestion-copy">
                      <strong>{t("patientPaymentHistory.suggestedBasedOnHistory")}</strong>
                      <span>{t("patientPaymentHistory.suggestionDetails", { amount: "62.500" })}</span>
                    </div>
                    <button type="button" className="payment-dashboard-suggestion-btn">{t("patientPaymentHistory.useSuggestion")}</button>
                  </div>
                </>
              )}
            </section>

            <aside className="payment-dashboard-quick">
              <div className="payment-dashboard-quick-header">
                <div>
                  <h2>{t("patientPaymentHistory.quickPayment")}</h2>
                  <span>{t(selectedCount === 1 ? "patientPaymentHistory.selectedItem" : "patientPaymentHistory.selectedItems", { count: selectedCount })}</span>
                </div>
                <span className={`payment-quick-badge is-${quickStatus}`}>{t(`patientPaymentHistory.status.${quickStatus}`)}</span>
              </div>
              {selectedVisit && selectedVisitPayment ? (
                <>
                  <div className="payment-dashboard-selected">
                    <span>{t("patientPaymentHistory.total")}</span>
                    <strong>{selectedVisitPayment.total.toFixed(3)} TND</strong>
                    <small>
                      {selectedVisit.reason || selectedVisit.consultationType || t("consultationPage.payment.consultation")}
                      {" · "}
                      {t("patientPaymentHistory.remaining")}: {selectedVisitPayment.remaining.toFixed(3)} TND
                    </small>
                  </div>
                  <span className="payment-dashboard-shortcuts-label">{t("patientPaymentHistory.amountShortcuts")}</span>
                  <div className="payment-dashboard-shortcuts">
                    <button type="button" className={paymentShortcut === "full" ? "is-active" : ""} onClick={() => { setPaymentShortcut("full"); setPaymentAmount(selectedVisitPayment.remaining.toFixed(3)); }}>{t("patientPaymentHistory.fullRemaining")}</button>
                    <button type="button" className={paymentShortcut === "half" ? "is-active" : ""} onClick={() => { setPaymentShortcut("half"); setPaymentAmount(Math.min(selectedVisitPayment.total / 2, selectedVisitPayment.remaining).toFixed(3)); }}>{t("patientPaymentHistory.half")}</button>
                    <button type="button" className={paymentShortcut === "custom" ? "is-active" : ""} onClick={() => { setPaymentShortcut("custom"); setPaymentAmount(selectedVisitPayment.remaining.toFixed(3)); }}>{t("patientPaymentHistory.custom")}</button>
                  </div>
                  <form className="payment-entry-form" onSubmit={handleAddPayment}>
                    <label className="payment-quick-field">
                      <span>{t("patientPaymentHistory.paymentAmount")}</span>
                      <div className="payment-dashboard-amount-control">
                        <input
                          type="number"
                          min="0.001"
                          max={selectedVisitPayment.remaining}
                          step="0.001"
                          required
                          value={quickAmount}
                          onChange={(event) => { setPaymentShortcut("custom"); setPaymentAmount(event.target.value); }}
                          disabled={!selectedVisitPayment.remaining || loading}
                        />
                        <span>TND</span>
                      </div>
                    </label>
                    <label className="payment-quick-field">
                      <span>{t("patientPaymentHistory.method")}</span>
                      <select value={paymentMethod} onChange={(event) => setPaymentMethod(event.target.value)} disabled={loading}>
                        <option value="cash">{t("patientPaymentHistory.paymentMethods.cash")}</option>
                        <option value="card">{t("patientPaymentHistory.paymentMethods.card")}</option>
                        <option value="cheque">{t("patientPaymentHistory.paymentMethods.cheque")}</option>
                        <option value="bank_transfer">{t("patientPaymentHistory.paymentMethods.bank_transfer")}</option>
                        <option value="insurance">{t("patientPaymentHistory.paymentMethods.insurance")}</option>
                      </select>
                    </label>
                    <label className="payment-quick-field">
                      <span>{t("patientPaymentHistory.allocationMode")}</span>
                      <select value={allocationMode} onChange={(event) => setAllocationMode(event.target.value)} disabled={loading}>
                        <option value="oldest">{t("patientPaymentHistory.allocationOldest")}</option>
                        <option value="manual">{t("patientPaymentHistory.allocationManual")}</option>
                      </select>
                    </label>
                    <div className="payment-dashboard-reference-fields">
                      <label className="payment-quick-field">
                        <span>{t("patientPaymentHistory.referenceNo")}</span>
                        <input value={paymentReference} onChange={(event) => setPaymentReference(event.target.value)} placeholder={t("patientPaymentHistory.optional")} disabled={loading} />
                      </label>
                      <label className="payment-quick-field">
                        <span>{t("patientPaymentHistory.internalNote")}</span>
                        <input value={paymentNote} onChange={(event) => setPaymentNote(event.target.value)} placeholder={t("patientPaymentHistory.optional")} disabled={loading} />
                      </label>
                    </div>
                    <label className="payment-dashboard-installment-toggle">
                      <span className="payment-dashboard-installment-icon" aria-hidden="true">↗</span>
                      <span><strong>{t("patientPaymentHistory.linkedInstallment")}</strong><small>{t("patientPaymentHistory.applyNextInstallment", { amount: Number(quickAmount || 0).toFixed(3) })}</small></span>
                      <input type="checkbox" checked={linkedInstallment} onChange={(event) => setLinkedInstallment(event.target.checked)} />
                    </label>
                    <button
                      className="payment-btn"
                      type="submit"
                      disabled={
                        !selectedVisitPayment.remaining ||
                        loading
                      }
                    >
                      {loading || fetching ? t("consultationPage.payment.saving") : t("patientPaymentHistory.recordPayment", { amount: Number(quickAmount || 0).toFixed(3) })}
                    </button>
                    <p className="payment-dashboard-quick-note">{t("patientPaymentHistory.paymentWillBeRecorded")}</p>
                  </form>
                </>
              ) : (
                <div className="payment-dashboard-selected">
                  <span>{t("patientPaymentHistory.total")}</span>
                  <strong>0.000 TND</strong>
                  <small>{t("patientPaymentHistory.selectVisit")}</small>
                </div>
              )}
            </aside>
          </div>
        )}

        {dashboardTab === "history" && (
          <section className="payment-dashboard-section payment-ledger-view" ref={paymentLedgerRef}>
            <div className="payment-ledger-today">
              <div className="payment-ledger-today-total"><span>{t("patientPaymentHistory.cashCollectedToday")}</span><strong>{paymentsToday.reduce((sum, entry) => sum + Number(entry.amount || 0), 0).toFixed(3)} TND</strong><small>{t("patientPaymentHistory.paymentsRecordedToday", { count: paymentsToday.length })}</small></div>
              {["cash", "card", "cheque"].map((method) => <div className="payment-ledger-method-total" key={method}><span>{t(`patientPaymentHistory.paymentMethods.${method}`)}</span><strong>{paymentMethodTotals[method].toFixed(3)}</strong></div>)}
              <button type="button" onClick={() => setReceiptShareMessage(t("patientPaymentHistory.dailyClosingUnavailable"))}>{t("patientPaymentHistory.openDailyClosing")}</button>
            </div>
            <header className="payment-ledger-header">
              <div><h2>{t("patientPaymentHistory.tabs.paymentHistory")}</h2><p>{t("patientPaymentHistory.paymentLedgerSubtitle")}</p></div>
              <div><button type="button" onClick={exportPaymentHistoryCsv}>{t("patientPaymentHistory.exportCsv")}</button><button type="button" onClick={downloadPaymentHistoryPdf}>{t("patientPaymentHistory.exportPdf")}</button></div>
            </header>
            <div className="payment-ledger-filters">
              <input value={paymentHistorySearch} onChange={(event) => setPaymentHistorySearch(event.target.value)} placeholder={t("patientPaymentHistory.searchReceipts")} aria-label={t("patientPaymentHistory.searchReceipts")} />
              <select value={paymentHistoryMethodFilter} onChange={(event) => setPaymentHistoryMethodFilter(event.target.value)} aria-label={t("patientPaymentHistory.allMethods")}>
                <option value="all">{t("patientPaymentHistory.allMethods")}</option>
                {Object.keys(paymentMethodTotals).concat(["bank_transfer", "insurance"]).map((method) => <option key={method} value={method}>{t(`patientPaymentHistory.paymentMethods.${method}`)}</option>)}
              </select>
              <select value={paymentHistoryUserFilter} onChange={(event) => setPaymentHistoryUserFilter(event.target.value)} aria-label={t("patientPaymentHistory.allUsers")}>
                <option value="all">{t("patientPaymentHistory.allUsers")}</option>
                {paymentHistoryUsers.map((user) => <option key={user} value={user}>{user}</option>)}
              </select>
              <select value={paymentHistoryPlanFilter} onChange={(event) => setPaymentHistoryPlanFilter(event.target.value)} aria-label={t("patientPaymentHistory.allPlans")}>
                <option value="all">{t("patientPaymentHistory.allPlans")}</option>
                {createdPlans.map((plan) => <option key={plan.id} value={String(plan.id)}>{plan.number}</option>)}
              </select>
              <div><input type="date" value={paymentHistoryFrom} onChange={(event) => setPaymentHistoryFrom(event.target.value)} aria-label={t("patientPaymentHistory.startDate")} /><span>–</span><input type="date" value={paymentHistoryTo} onChange={(event) => setPaymentHistoryTo(event.target.value)} aria-label={t("patientPaymentHistory.endDate")} /></div>
            </div>
            {receiptShareMessage && <p className="payment-ledger-feedback" role="status">{receiptShareMessage}</p>}
            {filteredPayments.length ? (
              <div className="payment-ledger-months">
                {Object.entries(paymentMonthGroups).map(([monthKey, entries]) => (
                <div className="payment-ledger-table-wrap" key={monthKey}>
                <div className="payment-ledger-month"><strong>{monthKey === "undated" ? t("patientPaymentHistory.date") : new Date(`${monthKey}-01T12:00:00`).toLocaleDateString(i18n.resolvedLanguage || i18n.language, { month: "long", year: "numeric" })}</strong><span>{t("patientPaymentHistory.subtotal")} · {entries.filter((entry) => !isVoidedPayment(entry)).reduce((sum, entry) => sum + Number(entry.amount || 0), 0).toFixed(3)} TND</span></div>
                <table className="payment-ledger-table">
                  <colgroup>
                    <col className="payment-ledger-col-date" />
                    <col className="payment-ledger-col-receipt" />
                    <col className="payment-ledger-col-item" />
                    <col className="payment-ledger-col-installment" />
                    <col className="payment-ledger-col-method" />
                    <col className="payment-ledger-col-amount" />
                    <col className="payment-ledger-col-actor" />
                    <col className="payment-ledger-col-status" />
                    <col className="payment-ledger-col-actions" />
                  </colgroup>
                  <thead><tr><th>{t("patientPaymentHistory.date")}</th><th>{t("patientPaymentHistory.receiptNo")}</th><th>{t("patientPaymentHistory.linkedItem")}</th><th>{t("patientPaymentHistory.installment")}</th><th>{t("patientPaymentHistory.method")}</th><th>{t("patientPaymentHistory.amount")}</th><th>{t("patientPaymentHistory.recordedBy")}</th><th>{t("patientPaymentHistory.status.label")}</th><th aria-label={t("patientPaymentHistory.moreActions")}></th></tr></thead>
                  <tbody>{entries.map((entry) => (
                    <Fragment key={entry.id}>
                      <tr>
                        <td>{formatPlanDate(getVisitDateKey({ consultationDateTime: entry.paidAt || entry.visitDate }))}</td>
                        <td><strong>{entry.receiptNo || "—"}</strong></td>
                        <td>{entry.description || t("consultationPage.payment.consultation")}</td>
                        <td>{entry.installmentNumber ? `${entry.installmentNumber} / ${entry.installmentCount || "—"}` : "—"}</td>
                        <td>{t(`patientPaymentHistory.paymentMethods.${entry.method || "cash"}`, { defaultValue: entry.method || t("patientPaymentHistory.paymentMethods.cash") })}</td>
                        <td><strong>{Number(entry.amount || 0).toFixed(3)} TND</strong></td>
                        <td>
                          <div className="payment-ledger-actor">
                            <span className="payment-ledger-actor-avatar" aria-hidden="true">{getActorInitials(entry.recordedBy) || "?"}</span>
                            <span className="payment-ledger-actor-copy">
                              <strong>{entry.recordedBy || "—"}</strong>
                              {entry.recordedByRole && <small>{getActorRoleLabel(entry.recordedByRole)}</small>}
                            </span>
                          </div>
                        </td>
                        <td><span className={`payment-ledger-status is-${entry.status}`}><span aria-hidden="true">{isVoidedPayment(entry) ? "!" : "✓"}</span>{t(`patientPaymentHistory.${entry.status}`, { defaultValue: t("patientPaymentHistory.completed") })}</span></td>
                        <td><button type="button" className="payment-ledger-more" aria-expanded={expandedPaymentId === entry.id} aria-label={t("patientPaymentHistory.moreActions")} onClick={() => setExpandedPaymentId((current) => current === entry.id ? null : entry.id)}>···</button></td>
                      </tr>
                      {expandedPaymentId === entry.id && (
                        <tr className="payment-ledger-details-row"><td colSpan="9">
                          <div className="payment-ledger-details">
                            <div><strong>{t("patientPaymentHistory.paymentAllocation")}</strong><span>{entry.allocations.length ? entry.allocations.map((allocation) => `${allocation.description || allocation.item || ""} ${Number(allocation.amount || 0).toFixed(3)} TND`).join(" · ") : t("patientPaymentHistory.noAllocationDetails")}</span></div>
                            <div><strong>{t("patientPaymentHistory.auditTrail")}</strong><span>{t("patientPaymentHistory.recordedAt", { date: formatPlanDate(getVisitDateKey({ consultationDateTime: entry.paidAt || entry.visitDate })) })} · {t("patientPaymentHistory.noChanges")}</span></div>
                            <div><button type="button" onClick={() => { setSelectedReceiptId(`receipt-${entry.id}`); setDashboardTab("receipts"); }}>{t("patientPaymentHistory.viewReceipt")}</button><button type="button" onClick={() => printReceipt(entry)}>{t("patientPaymentHistory.print")}</button><button type="button" disabled>{t("patientPaymentHistory.requestVoid")}</button><button type="button" disabled>{t("patientPaymentHistory.requestRefund")}</button></div>
                          </div>
                        </td></tr>
                      )}
                    </Fragment>
                  ))}</tbody>
                </table>
                </div>
                ))}
              </div>
            ) : <p className="payment-dashboard-empty">{t("patientPaymentHistory.noLedgerEntries")}</p>}
          </section>
        )}

        {dashboardTab === "plans" && (
          <section className="payment-dashboard-section payment-plans-view">
            <header className="payment-plans-view-header">
              <div><h2>{t("patientPaymentHistory.tabs.installmentPlans")}</h2><p>{t("patientPaymentHistory.plansSubtitle")}</p></div>
              <div className="payment-plans-view-actions">
                <select value={planStatusFilter} onChange={(event) => setPlanStatusFilter(event.target.value)} aria-label={t("patientPaymentHistory.allPlanStatuses")}>
                  <option value="all">{t("patientPaymentHistory.allPlanStatuses")}</option>
                  <option value="active">{t("patientPaymentHistory.active")}</option>
                  <option value="overdue">{t("patientPaymentHistory.overdue")}</option>
                  <option value="paused">{t("patientPaymentHistory.paused")}</option>
                  <option value="completed">{t("patientPaymentHistory.completed")}</option>
                </select>
                <button type="button" onClick={openPlanDrawer}>+ {t("patientPaymentHistory.createInstallmentPlan")}</button>
              </div>
            </header>
            <div className="payment-plans-management-layout">
              <aside className="payment-plan-cards-list">
                {visiblePlans.length ? visiblePlans.map((plan) => {
                  const isSelected = plan.id === selectedPlan?.id;
                  const planStatus = getSessionPlanStatus(plan);
                  const remainingAmount = Math.max(0, Number(plan.amount || 0) - Number(plan.paidAmount || 0));
                  const nextInstallment = plan.installments?.find((installment) => !installment.paidAt);
                  return (
                    <button type="button" className={`payment-plan-card${isSelected ? " is-selected" : ""}`} key={plan.id} onClick={() => setSelectedPlanId(plan.id)}>
                      <div className="payment-plan-card-top"><span className={`payment-plan-status is-${planStatus}`}>{t(`patientPaymentHistory.${planStatus === "active" ? "onPlan" : planStatus}`)}</span><small>{plan.number}</small></div>
                      <strong>{plan.items?.[0] || t("patientPaymentHistory.createInstallmentPlan")}</strong>
                      <span>{plan.items?.slice(1).join(" + ") || t("patientPaymentHistory.planItemsCount", { count: plan.itemCount })}</span>
                      <div className="payment-plan-card-progress"><i style={{ width: `${plan.amount ? Math.min(100, (Number(plan.paidAmount || 0) / plan.amount) * 100) : 0}%` }} /></div>
                      <div className="payment-plan-card-values"><span>{t("patientPaymentHistory.paidAmount")} <b>{Number(plan.paidAmount || 0).toFixed(3)}</b></span><span>{t("patientPaymentHistory.remainingAmount")} <b>{remainingAmount.toFixed(3)} TND</b></span></div>
                      {nextInstallment && <small className="payment-plan-card-next">{t("patientPaymentHistory.nextDueAmount", { date: formatPlanDate(nextInstallment.date), amount: Number(nextInstallment.amount || 0).toFixed(3) })}</small>}
                    </button>
                  );
                }) : <p className="payment-dashboard-empty">{t("patientPaymentHistory.noPlans")}</p>}
                <div className="payment-plan-empty-preview">{t("patientPaymentHistory.emptyPlanPreview")}</div>
              </aside>
              {selectedPlan ? (
                <article className="payment-plan-details">
                  <header className="payment-plan-details-header">
                    <div><span>{t("patientPaymentHistory.planReferenceStatus", { reference: selectedPlan.number, status: t(`patientPaymentHistory.${getSessionPlanStatus(selectedPlan) === "active" ? "active" : getSessionPlanStatus(selectedPlan)}`).toUpperCase() })}</span><h3>{selectedPlan.items?.[0] || t("patientPaymentHistory.createInstallmentPlan")}</h3><p>{t("patientPaymentHistory.planCreatedActivity")} · {formatPlanDate(getVisitDateKey({ consultationDateTime: selectedPlan.createdAt }))}</p></div>
                    <div>
                      <button type="button" onClick={() => togglePlanStatus(selectedPlan)}>{t(selectedPlan.status === "paused" ? "patientPaymentHistory.resumePlan" : "patientPaymentHistory.pausePlan")}</button>
                      <button type="button" onClick={() => requestPlanEdit(selectedPlan)}>{t("patientPaymentHistory.requestEdit")}</button>
                    </div>
                  </header>
                  {selectedPlan.editRequested && <p className="payment-plan-edit-requested">{t("patientPaymentHistory.editRequested")}</p>}
                  <div className="payment-plan-progress-metrics">
                    <article><span>{t("patientPaymentHistory.planTotal")}</span><strong>{Number(selectedPlan.amount).toFixed(3)} TND</strong></article>
                    <article><span>{t("patientPaymentHistory.paidAmount")}</span><strong>{selectedPlanPaid.toFixed(3)} TND</strong></article>
                    <article><span>{t("patientPaymentHistory.remainingAmount")}</span><strong>{selectedPlanRemaining.toFixed(3)} TND</strong></article>
                    <article><span>{t("patientPaymentHistory.progress")}</span><strong>{selectedPlanProgress.toFixed(0)}%</strong></article>
                  </div>
                  <div className="payment-plan-progress-track"><span style={{ width: `${selectedPlanProgress}%` }} /></div>
                      <div className="payment-plan-timeline-heading"><h4>{t("patientPaymentHistory.installmentTimeline")}</h4>{selectedPlan.installments?.some((installment) => !installment.paidAt) && <button type="button" onClick={() => payNextPlanInstallment(selectedPlan)}>{t("patientPaymentHistory.payNextInstallment", { amount: Number(selectedPlan.installments.find((installment) => !installment.paidAt)?.amount || 0).toFixed(3) })}</button>}</div>
                  <div className="payment-plan-timeline">
                    {(selectedPlan.installments || []).map((installment, index) => {
                      const status = installment.paidAt ? "paid" : installment.date < todayDateKey ? "overdue" : installment.date <= dueSoonDateKey ? "due" : "upcoming";
                      return <div className={`payment-plan-timeline-row is-${status}`} key={installment.id}><span className="payment-plan-timeline-index">{installment.paidAt ? "✓" : index + 1}</span><div><strong>{t("patientPaymentHistory.installmentNumber", { number: index + 1 })}</strong><small>{formatPlanDate(installment.date)}</small></div><b>{Number(installment.amount || 0).toFixed(3)} TND</b><span className={`payment-plan-due-badge is-${status === "due" ? "due" : status}`}>{t(`patientPaymentHistory.${status === "due" ? "dueSoon" : status}`)}</span><button type="button" aria-label={t("patientPaymentHistory.moreActions")}>···</button></div>;
                    })}
                  </div>
                  <section className="payment-plan-activity"><h4>{t("patientPaymentHistory.recentPlanActivity")}</h4><p><span>{formatPlanDate(getVisitDateKey({ consultationDateTime: selectedPlan.createdAt }))}</span>{t("patientPaymentHistory.planCreatedActivity")}</p></section>
                </article>
              ) : (
                <article className="payment-plan-details payment-plan-details-empty"><p>{t("patientPaymentHistory.emptyPlanPreview")}</p></article>
              )}
            </div>
          </section>
        )}

        {dashboardTab === "receipts" && (
          <section className="payment-dashboard-section payment-receipts-view">
            <header className="payment-receipts-header">
              <div><h2>{t("patientPaymentHistory.tabs.receiptsDocuments")}</h2><p>{t("patientPaymentHistory.receiptsSubtitle")}</p></div>
              <button type="button" onClick={() => setSelectedReceiptId("statement-current")}>{t("patientPaymentHistory.generateStatement")}</button>
            </header>
            <div className="payment-receipts-layout">
              <aside className="payment-receipt-document-list">
                {receiptDocuments.map((document) => {
                  const title = document.type === "receipt"
                    ? t("patientPaymentHistory.receipt")
                    : document.type === "plan"
                    ? t("patientPaymentHistory.planAgreement")
                    : t("patientPaymentHistory.accountStatement");
                  const detail = document.type === "receipt"
                    ? document.entry.receiptNo || formatPlanDate(getVisitDateKey({ consultationDateTime: document.entry.paidAt || document.entry.visitDate }))
                    : document.type === "plan"
                    ? document.plan.number
                    : t("patientPaymentHistory.remainingBalance");
                  return (
                    <button type="button" className={`payment-receipt-document-card${selectedReceipt?.id === document.id ? " is-selected" : ""}`} key={document.id} onClick={() => { setSelectedReceiptId(document.id); setReceiptShareMessage(""); }}>
                      <span className="payment-receipt-document-icon">{document.type === "receipt" ? "RE" : document.type === "plan" ? "PL" : "AC"}</span>
                      <span><strong>{title}</strong><small>{detail}</small></span>
                      <b>{Number(document.amount || 0).toFixed(3)} TND</b>
                    </button>
                  );
                })}
              </aside>
              {selectedReceipt ? (
                <div className="payment-receipt-preview-shell">
                  <header className="payment-receipt-preview-toolbar">
                    <strong>{t(selectedReceipt.type === "receipt" ? "patientPaymentHistory.receiptPreview" : selectedReceipt.type === "plan" ? "patientPaymentHistory.planPreview" : "patientPaymentHistory.statementPreview")}</strong>
                    <div><button type="button" onClick={sharePaymentDocument}>{t("patientPaymentHistory.send")}</button><button type="button" className="is-primary" onClick={downloadReceiptPdf}>{t("patientPaymentHistory.downloadPdf")}</button></div>
                  </header>
                  {receiptShareMessage && <p className="payment-receipt-feedback" role="status">{receiptShareMessage}</p>}
                  <article className="payment-receipt-preview" ref={receiptPreviewRef}>
                    <header className="payment-receipt-brand">
                      <div className="payment-receipt-brand-copy">
                        <strong>{doctorDisplayName}</strong>
                        {doctorProfile?.specialty && <small>{doctorProfile.specialty}</small>}
                        {(doctorProfile?.clinicAddress || doctorProfile?.practicePlace || doctorProfile?.address) && (
                          <small>{doctorProfile.clinicAddress || doctorProfile.practicePlace || doctorProfile.address}</small>
                        )}
                        {(doctorProfile?.email || doctorProfile?.phone) && (
                          <small>{[doctorProfile.email, doctorProfile.phone].filter(Boolean).join(" · ")}</small>
                        )}
                      </div>
                      <span aria-hidden="true">{doctorDisplayName.split(/\s+/).map((part) => part[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "DR"}</span>
                    </header>
                    {selectedReceipt.type === "receipt" && (
                      <>
                        <div className="payment-receipt-visual">
                          <div className="payment-receipt-reference-row">
                            <strong>{t("patientPaymentHistory.receipt")}</strong>
                            <span>{receiptEntry?.receiptNo || receiptEntry?.receiptNumber || receiptEntry?.referenceNo || "—"}</span>
                          </div>

                          <div className="payment-receipt-patient-box">
                            <div className="payment-receipt-patient-col">
                              <span>{t("patientPaymentHistory.patient")}</span>
                              <strong>{patientDisplayName || "—"}</strong>
                              <small>{patientDisplayReference || "—"}</small>
                            </div>
                            <div className="payment-receipt-patient-col payment-receipt-patient-col--right">
                              <span>{t("patientPaymentHistory.date")}</span>
                              <strong>{formatPlanDate(getVisitDateKey({ consultationDateTime: receiptEntry?.paidAt || receiptEntry?.visitDate }))}</strong>
                              <small>{receiptEntry?.recordedBy || doctorDisplayName}</small>
                            </div>
                          </div>

                          <div className="payment-receipt-line-block">
                            {receiptAllocations.map((allocation, index) => (
                              <div className={`payment-receipt-line${index > 0 ? " is-detail" : ""}`} key={`${allocation.kind || "item"}-${allocation.code || allocation.description || index}`}>
                                <span>{allocation.description || allocation.designation || allocation.name || t("consultationPage.payment.consultation")}{allocation.code ? ` · ${allocation.code}` : ""}</span>
                                <strong>{Number(allocation.amount || 0).toFixed(3)} TND</strong>
                              </div>
                            ))}
                          </div>

                          <div className="payment-receipt-total-row">
                            <span>{t("patientPaymentHistory.amount")}</span>
                            <strong>{Number(receiptEntry?.amount || selectedReceipt.amount || 0).toFixed(3)} TND</strong>
                          </div>

                          <div className="payment-receipt-remaining-row">
                            <span>{t("patientPaymentHistory.remainingBalance")}</span>
                            <strong>{patientRemaining.toFixed(3)} TND</strong>
                          </div>

                          <div className="payment-receipt-footer-note">
                            {t(`patientPaymentHistory.paymentMethods.${receiptEntry?.method || "cash"}`, { defaultValue: receiptEntry?.method || "cash" })} · Medilink
                          </div>
                        </div>
                      </>
                    )}
                    {selectedReceipt.type === "plan" && (
                      <>
                        <div className="payment-receipt-title-row"><strong>{t("patientPaymentHistory.planAgreement")} · {selectedReceipt.plan.number}</strong><span>{formatPlanDate(getVisitDateKey({ consultationDateTime: selectedReceipt.plan.createdAt }))}</span></div>
                        <div className="payment-receipt-patient"><div><small>{t("patientPaymentHistory.patient")}</small><strong>{patientDisplayName || "—"}</strong><span>{patientDisplayReference}</span></div><div><small>{t("patientPaymentHistory.planItemsCount", { count: selectedReceipt.plan.itemCount })}</small><strong>{selectedReceipt.plan.frequency === "weekly" ? t("patientPaymentHistory.weekly") : t("patientPaymentHistory.monthly")}</strong></div></div>
                        {(selectedReceipt.plan.itemDetails || selectedReceipt.plan.items.map((description) => ({ description, amount: 0 }))).map((item, index) => <div className="payment-receipt-line" key={`${item.description}-${index}`}><span>{item.description}</span><strong>{Number(item.amount || 0).toFixed(3)} TND</strong></div>)}
                        {(selectedReceipt.plan.installments || []).map((installment, index) => <div className="payment-receipt-line is-detail" key={installment.id}><span>{t("patientPaymentHistory.installmentNumber", { number: index + 1 })} · {formatPlanDate(installment.date)}</span><strong>{Number(installment.amount || 0).toFixed(3)} TND</strong></div>)}
                        <div className="payment-receipt-total"><span>{t("patientPaymentHistory.planTotal")}</span><strong>{Number(selectedReceipt.plan.amount).toFixed(3)} TND</strong></div>
                      </>
                    )}
                    {selectedReceipt.type === "statement" && (
                      <>
                        <div className="payment-receipt-title-row"><strong>{t("patientPaymentHistory.accountStatement")}</strong><span>{formatPlanDate(localTodayKey)}</span></div>
                        <div className="payment-receipt-patient"><div><small>{t("patientPaymentHistory.patient")}</small><strong>{patientDisplayName || "—"}</strong><span>{patientDisplayReference}</span></div><div><small>{t("patientPaymentHistory.totalConsultations")}</small><strong>{patientTotal.toFixed(3)} TND</strong></div></div>
                        {displayVisits.map((visit) => {
                          const payment = getVisitPayment(visit);
                          return <div className="payment-receipt-line" key={visit.id}><span>{visit.reason || visit.consultationType || t("consultationPage.payment.consultation")}</span><strong>{payment.remaining.toFixed(3)} TND</strong></div>;
                        })}
                        <div className="payment-receipt-total"><span>{t("patientPaymentHistory.remainingBalance")}</span><strong>{patientRemaining.toFixed(3)} TND</strong></div>
                      </>
                    )}
                  </article>
                </div>
              ) : <p className="payment-dashboard-empty">{t("patientPaymentHistory.noReceiptDocuments")}</p>}
            </div>
          </section>
        )}
        {isPlanDrawerOpen && (
          <div className="payment-plan-overlay" onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsPlanDrawerOpen(false);
          }}>
            <aside className="payment-plan-drawer" role="dialog" aria-modal="true" aria-labelledby="payment-plan-title">
              <header className="payment-plan-drawer-header">
                <div>
                  <span className="payment-plan-eyebrow">{t("patientPaymentHistory.planCreateEyebrow")}</span>
                  <h2 id="payment-plan-title">{t("patientPaymentHistory.createInstallmentPlan")}</h2>
                  <p>{t("patientPaymentHistory.planStepSubtitle", {
                    step: planStep,
                    label: t(`patientPaymentHistory.planSteps.${["items", "schedule", "confirm"][planStep - 1]}`),
                  })}</p>
                </div>
                <button type="button" className="payment-plan-close" onClick={() => setIsPlanDrawerOpen(false)} aria-label={t("patientPaymentHistory.closePlanWizard")}>×</button>
              </header>
              <div className="payment-plan-stepper" aria-label={t("patientPaymentHistory.planStepSubtitle", {
                step: planStep,
                label: t(`patientPaymentHistory.planSteps.${["items", "schedule", "confirm"][planStep - 1]}`),
              })}>
                {["items", "schedule", "confirm"].map((step, index) => (
                  <div className={`payment-plan-step${planStep >= index + 1 ? " is-active" : ""}`} key={step}>
                    <span>{index + 1}</span>
                    <strong>{t(`patientPaymentHistory.planSteps.${step}`)}</strong>
                  </div>
                ))}
              </div>
              <div className="payment-plan-drawer-body">
                {planStep === 1 && (
                  <section className="payment-plan-step-content">
                    <h3>{t("patientPaymentHistory.selectUnpaidItems")}</h3>
                    <p>{t("patientPaymentHistory.cnamCoverageOnly")}</p>
                    {planEligibleVisits.length ? (
                      <div className="payment-plan-item-list">
                        {planEligibleVisits.map((visit) => {
                          const visitPayment = getVisitPayment(visit);
                          const checked = planSelectedVisitIds.includes(String(visit.id));
                          const itemTitle = visit.reason || visit.consultationType || t("consultationPage.payment.consultation");
                          const actNames = (visit.medicalActs || [])
                            .map((act) => act.designation || act.name || act.description)
                            .filter((name) => name && name.toLowerCase() !== String(itemTitle).toLowerCase());
                          const actCodes = (visit.medicalActs || []).map((act) => act.code).filter(Boolean);
                          const details = [
                            formatPlanDate(getVisitDateKey(visit)),
                            Number(visit.consultationPrice || 0) > 0 ? t("patientPaymentHistory.consultationFee") : "",
                            actNames.length ? actNames.join(" + ") : (visit.medicalActs || []).length ? t("patientPaymentHistory.act") : "",
                            actCodes.join(", "),
                          ].filter(Boolean).join(" · ");
                          return (
                            <label className="payment-plan-item" key={visit.id}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => setPlanSelectedVisitIds((current) => checked
                                  ? current.filter((id) => id !== String(visit.id))
                                  : [...current, String(visit.id)])}
                              />
                              <span className="payment-plan-item-copy">
                                <strong>{itemTitle}</strong>
                                <small>{details}</small>
                              </span>
                              <strong className="payment-plan-item-amount">{visitPayment.remaining.toFixed(3)} TND</strong>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="payment-dashboard-empty">{t("patientPaymentHistory.noOutstandingItems")}</p>
                    )}
                    <button type="button" className="payment-plan-future-toggle" onClick={() => setIsFuturePlanItemOpen((open) => !open)}>
                      {t("patientPaymentHistory.planFutureItem")}
                    </button>
                    {isFuturePlanItemOpen && (
                      <div className="payment-plan-future-fields">
                        <label>
                          <span>{t("patientPaymentHistory.futureItemName")}</span>
                          <input value={futurePlanItemName} onChange={(event) => setFuturePlanItemName(event.target.value)} />
                        </label>
                        <label>
                          <span>{t("patientPaymentHistory.estimatedAmount")}</span>
                          <input type="number" min="0" step="0.001" value={futurePlanItemAmount} onChange={(event) => setFuturePlanItemAmount(event.target.value)} />
                        </label>
                      </div>
                    )}
                    <div className="payment-plan-total-card">
                      <span>{t("patientPaymentHistory.selectedPatientShare")}</span>
                      <strong>{planSelectedAmount.toFixed(3)} TND</strong>
                      <small>{t("patientPaymentHistory.coveredByCNAM")}: {planCoveredAmount.toFixed(3)} TND</small>
                    </div>
                  </section>
                )}
                {planStep === 2 && (
                  <section className="payment-plan-step-content">
                    <div className="payment-plan-mode-grid">
                      {[
                        ["per-visit", "perVisit", "perVisitDescription"],
                        ["monthly", "monthlyFixed", "monthlyDescription"],
                        ["flexible", "flexible", "flexibleDescription"],
                      ].map(([value, title, description]) => (
                        <button
                          type="button"
                          key={value}
                          className={planScheduleType === value ? "is-active" : ""}
                          aria-pressed={planScheduleType === value}
                          onClick={() => {
                            setPlanScheduleType(value);
                            const frequency = value === "per-visit" ? "weekly" : "monthly";
                            const count = value === "per-visit"
                              ? Math.max(1, planSelectedVisits.length)
                              : planInstallmentCount;
                            setPlanFrequency(frequency);
                            setPlanInstallmentCount(count);
                            generatePlanSchedule(planSchedulePrincipal, count, planFirstDueDate, frequency, planDayOfMonth);
                          }}
                        >
                          <strong>{t(`patientPaymentHistory.${title}`)}</strong>
                          <small>{t(`patientPaymentHistory.${description}`)}</small>
                        </button>
                      ))}
                    </div>
                    <div className="payment-plan-schedule-settings">
                      <div className="payment-plan-schedule-fields">
                        <label>
                          <span>{t(planScheduleType === "monthly" ? "patientPaymentHistory.numberOfMonths" : "patientPaymentHistory.installmentCount")}</span>
                          <input
                            type="number"
                            min="1"
                            max="24"
                            value={planInstallmentCount}
                            onChange={(event) => {
                              const count = Math.min(24, Math.max(1, Number(event.target.value) || 1));
                              setPlanInstallmentCount(count);
                              generatePlanSchedule(planSchedulePrincipal, count);
                            }}
                          />
                        </label>
                        <label>
                          <span>{t("patientPaymentHistory.startDateLabel")}</span>
                          <input
                            type="date"
                            value={planFirstDueDate}
                            onChange={(event) => {
                              setPlanFirstDueDate(event.target.value);
                              generatePlanSchedule(planSchedulePrincipal, planInstallmentCount, event.target.value);
                            }}
                          />
                        </label>
                        <label>
                          <span>{t("patientPaymentHistory.dayOfMonth")}</span>
                          <input
                            type="number"
                            min="1"
                            max="31"
                            value={planDayOfMonth}
                            disabled={planFrequency === "weekly"}
                            onChange={(event) => {
                              const day = Math.min(31, Math.max(1, Number(event.target.value) || 1));
                              setPlanDayOfMonth(day);
                              generatePlanSchedule(planSchedulePrincipal, planInstallmentCount, planFirstDueDate, planFrequency, day);
                            }}
                          />
                        </label>
                      </div>
                      <div className="payment-plan-calculated">
                        <span>{t("patientPaymentHistory.calculatedInstallment")}</span>
                        <strong>{t(planFrequency === "weekly" ? "patientPaymentHistory.amountPerInstallment" : "patientPaymentHistory.monthlyAmount", { amount: (planSchedulePrincipal / Math.max(1, Number(planInstallmentCount))).toFixed(3) })}</strong>
                        <small>{t("patientPaymentHistory.lastInstallmentRemainder")}</small>
                      </div>
                    </div>
                    <label className="payment-plan-down-payment">
                      <input
                        type="checkbox"
                        checked={planDownPaymentEnabled}
                        onChange={(event) => {
                          const enabled = event.target.checked;
                          const amount = enabled ? (planSelectedAmount * 0.1).toFixed(3) : "0.000";
                          setPlanDownPaymentEnabled(enabled);
                          setPlanDownPaymentAmount(amount);
                          generatePlanSchedule(Math.max(0, planSelectedAmount - (Number(amount) || 0)), planInstallmentCount);
                        }}
                      />
                      <span><strong>{t("patientPaymentHistory.downPayment")}</strong><small>{t("patientPaymentHistory.downPaymentDescription")}</small></span>
                      {planDownPaymentEnabled && (
                        <input
                          type="number"
                          min="0"
                          max={planSelectedAmount}
                          step="0.001"
                          value={planDownPaymentAmount}
                          aria-label={t("patientPaymentHistory.downPayment")}
                          onChange={(event) => {
                            setPlanDownPaymentAmount(event.target.value);
                            generatePlanSchedule(Math.max(0, planSelectedAmount - (Number(event.target.value) || 0)), planInstallmentCount);
                          }}
                        />
                      )}
                    </label>
                    <div className="payment-plan-preview-heading">
                      <h3>{t("patientPaymentHistory.schedulePreview")}</h3>
                      <span>{t("patientPaymentHistory.dragAmountsEditable")}</span>
                    </div>
                    <div className="payment-plan-installment-list">
                      {planScheduleRows.map((installment, index) => (
                        <div
                          className="payment-plan-installment-row"
                          key={installment.id}
                          draggable
                          onDragStart={(event) => event.dataTransfer.setData("text/plain", installment.id)}
                          onDragOver={(event) => event.preventDefault()}
                          onDrop={(event) => {
                            event.preventDefault();
                            const draggedId = event.dataTransfer.getData("text/plain");
                            setPlanInstallments((current) => {
                              const from = current.findIndex((item) => item.id === draggedId);
                              const to = current.findIndex((item) => item.id === installment.id);
                              if (from < 0 || to < 0 || from === to) return current;
                              const reordered = [...current];
                              const [moved] = reordered.splice(from, 1);
                              reordered.splice(to, 0, moved);
                              return reordered;
                            });
                          }}
                        >
                          <span className="payment-plan-drag-handle" aria-hidden="true">⠿</span>
                          <strong className="payment-plan-row-number">#{index + 1}</strong>
                          <input
                            type="date"
                            value={installment.date}
                            aria-label={t("patientPaymentHistory.installmentNumber", { number: index + 1 }) + " · " + t("patientPaymentHistory.dueDate")}
                            onChange={(event) => setPlanInstallments((current) => current.map((item) => item.id === installment.id ? { ...item, date: event.target.value } : item))}
                          />
                          <div className="payment-plan-row-amount">
                            <input
                              type="number"
                              min="0"
                              step="0.001"
                              value={installment.amount}
                              aria-label={t("patientPaymentHistory.installmentNumber", { number: index + 1 }) + " · " + t("patientPaymentHistory.amount")}
                              onChange={(event) => setPlanInstallments((current) => current.map((item) => item.id === installment.id ? { ...item, amount: event.target.value } : item))}
                            />
                            <span>TND</span>
                          </div>
                          <button type="button" aria-label={t("patientPaymentHistory.removeInstallment")} onClick={() => setPlanInstallments((current) => current.filter((item) => item.id !== installment.id))}>×</button>
                        </div>
                      ))}
                    </div>
                    <button type="button" className="payment-plan-add-installment" onClick={() => {
                      const lastDate = planScheduleRows[planScheduleRows.length - 1]?.date || planFirstDueDate;
                      const nextDate = new Date(`${lastDate}T12:00:00`);
                      nextDate.setDate(nextDate.getDate() + (planFrequency === "weekly" ? 7 : 30));
                      setPlanInstallments((current) => [...current, { id: `${Date.now()}-new`, date: formatDateKey(nextDate), amount: "0.000" }]);
                    }}>{t("patientPaymentHistory.addInstallment")}</button>
                    <div className={`payment-plan-schedule-total${planScheduleBalanced ? " is-balanced" : " is-unbalanced"}`}>
                      <span>{t("patientPaymentHistory.scheduleTotal")}</span>
                      <strong>{planTotalScheduled.toFixed(3)} TND</strong>
                      <small>{planScheduleBalanced
                        ? t("patientPaymentHistory.balanced")
                        : t("patientPaymentHistory.notBalanced", { amount: Math.abs(planSelectedAmount - planTotalScheduled).toFixed(3) })}</small>
                    </div>
                  </section>
                )}
                {planStep === 3 && (
                  <section className="payment-plan-step-content">
                    <h3>{t("patientPaymentHistory.planSteps.confirm")}</h3>
                    <div className="payment-plan-confirm-summary">
                      <article><span>{t("patientPaymentHistory.planTotal")}</span><strong>{planSelectedAmount.toFixed(3)} TND</strong></article>
                      <article><span>{t("patientPaymentHistory.schedule")}</span><strong>{t("patientPaymentHistory.planScheduleSummary", {
                        count: planScheduleRows.length,
                        frequency: t(`patientPaymentHistory.${planFrequency}`),
                        date: formatPlanDate(planScheduleRows[0]?.date),
                      })}</strong></article>
                      <article><span>{t("patientPaymentHistory.firstDueDate")}</span><strong>{formatPlanDate(planScheduleRows[0]?.date)}</strong></article>
                    </div>
                    <div className="payment-plan-confirm-table-wrap">
                      <table className="payment-plan-confirm-table">
                        <thead><tr><th>#</th><th>{t("patientPaymentHistory.dueDate")}</th><th>{t("patientPaymentHistory.amount")}</th><th>{t("patientPaymentHistory.status.label")}</th></tr></thead>
                        <tbody>
                          {planDownPayment > 0 && <tr><td>0</td><td>{t("patientPaymentHistory.today")}</td><td><strong>{planDownPayment.toFixed(3)} TND</strong></td><td><span className="payment-plan-due-badge is-due">{t("patientPaymentHistory.dueSoon")}</span></td></tr>}
                          {planScheduleRows.map((installment, index) => {
                            const status = installment.date < todayDateKey ? "overdue" : installment.date <= dueSoonDateKey ? "due" : "upcoming";
                            return <tr key={installment.id}><td>{index + 1}</td><td>{formatPlanDate(installment.date)}</td><td><strong>{Number(installment.amount || 0).toFixed(3)} TND</strong></td><td><span className={`payment-plan-due-badge is-${status}`}>{t(`patientPaymentHistory.${status === "due" ? "dueSoon" : status === "overdue" ? "overdue" : "upcoming"}`)}</span></td></tr>;
                          })}
                        </tbody>
                        <tfoot><tr><th colSpan="2">{t("patientPaymentHistory.planTotal")}</th><th>{planTotalScheduled.toFixed(3)} TND</th><th>{t(planScheduleBalanced ? "patientPaymentHistory.balanced" : "patientPaymentHistory.notBalanced", { amount: Math.abs(planSelectedAmount - planTotalScheduled).toFixed(3) })}</th></tr></tfoot>
                      </table>
                    </div>
                    <label className="payment-plan-confirm-option is-locked">
                      <input type="checkbox" checked={planDiscountRequested} disabled onChange={(event) => setPlanDiscountRequested(event.target.checked)} />
                      <span><strong>{t("patientPaymentHistory.discountCourtesy")}</strong><small>{t("patientPaymentHistory.doctorOnly")} · {t("patientPaymentHistory.discountAuditReason")}</small></span>
                    </label>
                    <label className="payment-plan-confirm-option">
                      <input type="checkbox" checked={planPatientConsent} onChange={(event) => setPlanPatientConsent(event.target.checked)} />
                      <span><strong>{t("patientPaymentHistory.patientConsent")}</strong><small>{t("patientPaymentHistory.agreedAtClinic")} · {formatPlanDate(formatDateKey(new Date()))}</small></span>
                      <button type="button" className="payment-plan-signature-button" onClick={() => setPlanSignatureCaptured(true)}>{t(planSignatureCaptured ? "patientPaymentHistory.signatureCaptured" : "patientPaymentHistory.captureSignature")}</button>
                    </label>
                    <div className="payment-plan-approval-note">
                      <strong>{t("patientPaymentHistory.doctorApprovalRequested")}</strong>
                      <span>{t("patientPaymentHistory.doctorCanApprove")}</span>
                    </div>
                    <fieldset className="payment-plan-delivery-options">
                      <legend>{t("patientPaymentHistory.printSendPlan")}</legend>
                      {[["pdf", "pdfReceipt"], ["sms", "sms"], ["whatsapp", "whatsapp"], ["email", "email"]].map(([key, label]) => (
                        <label key={key}><input type="checkbox" checked={planSendOptions[key]} onChange={(event) => setPlanSendOptions((current) => ({ ...current, [key]: event.target.checked }))} /><span>{t(`patientPaymentHistory.${label}`)}</span></label>
                      ))}
                    </fieldset>
                    <p className="payment-plan-session-note">{t("patientPaymentHistory.planCreatedInSession")}</p>
                  </section>
                )}
              </div>
              <footer className="payment-plan-drawer-footer">
                <button type="button" className="payment-plan-cancel" onClick={() => setIsPlanDrawerOpen(false)}>{t("patientPaymentHistory.cancelPlan")}</button>
                {planStep > 1 && <button type="button" className="payment-plan-back" onClick={() => setPlanStep((step) => step - 1)}>{t("patientPaymentHistory.backPlan")}</button>}
                {planStep < 3 ? (
                  <button type="button" className="payment-plan-continue" disabled={planStep === 1 && planSelectedAmount <= 0 || planStep === 2 && !planScheduleBalanced} onClick={() => {
                    if (planStep === 1) {
                      const nextTotal = planSelectedVisits.reduce((sum, visit) => sum + getVisitPayment(visit).remaining, Number(futurePlanItemAmount) || 0);
                      const downPayment = planDownPaymentEnabled ? Number(planDownPaymentAmount) || 0 : 0;
                      generatePlanSchedule(Math.max(0, nextTotal - downPayment), planInstallmentCount);
                    }
                    setPlanStep((step) => step + 1);
                  }}>{t("patientPaymentHistory.continuePlan")}</button>
                ) : (
                  <button type="button" className="payment-plan-continue" disabled={!planScheduleBalanced || !planPatientConsent} onClick={createPlanFromDrawer}>{t("patientPaymentHistory.requestApproval")}</button>
                )}
              </footer>
            </aside>
          </div>
        )}
      </div>
    );
  }

  if (fetching) {
    return <div className="payment-tab">{t("consultationPage.payment.loading")}</div>;
  }

  return (
    <div className="payment-tab">
      <h3 className="payment-title">{t("consultationPage.payment.title")}</h3>

      {!patientId ? (
        <p className="payment-warning">
          {t("consultationPage.payment.saveFirst")}
        </p>
      ) : (
        <>
          <div className="payment-summary-grid">
            <div className="payment-summary-item">
              <span>{t("consultationPage.payment.totalVisits")}</span>
              <strong>{patientTotal.toFixed(3)} TND</strong>
            </div>
            <div className="payment-summary-item">
              <span>{t("consultationPage.payment.paidTotal")}</span>
              <strong>{patientPaid.toFixed(3)} TND</strong>
            </div>
            <div className="payment-summary-item payment-summary-remaining">
              <span>{t("consultationPage.payment.remaining")}</span>
              <strong>{patientRemaining.toFixed(3)} TND</strong>
            </div>
          </div>

          <label className="payment-select-label" htmlFor="payment-visit-select">
            {t("consultationPage.payment.chooseVisit")}
          </label>
          <select
            id="payment-visit-select"
            className="payment-select"
            value={selectedConsultationId}
            disabled={loadingConsultations || consultations.length === 0}
            onChange={(event) => {
              setSelectedConsultationId(event.target.value);
              setPaymentAmount("");
            }}
          >
            {consultations.length === 0 ? (
              <option value="">
                {loadingConsultations
                  ? t("consultationPage.payment.loadingVisits")
                  : consultationsError
                  ? t("consultationPage.payment.consultationsError")
                  : t("consultationPage.payment.noConsultations")}
              </option>
            ) : consultations.map((visit) => (
                <option key={visit.id} value={visit.id}>
                  {new Date(visit.consultationDateTime || visit.createdAt).toLocaleDateString()} · {getConsultationTotal(visit).toFixed(3)} TND
                </option>
              ))}
          </select>

          {consultationData && !fetching && (
            <>
              <div className="payment-row">
                <span>{t("consultationPage.payment.consultation")}</span>
                <span>{consultationPrice.toFixed(3)} TND</span>
              </div>
              <div className="payment-row">
                <span>{t("consultationPage.payment.medicalActs")}</span>
                <span>{medicalActsTotal.toFixed(3)} TND</span>
              </div>
              <div className="payment-row payment-total">
                <span>{t("consultationPage.payment.remainingVisit")}</span>
                <span>{remaining.toFixed(3)} TND</span>
              </div>
              <form className="payment-entry-form" onSubmit={handleAddPayment}>
                <label>
                  <span>{t("consultationPage.payment.paymentAmount")}</span>
                  <input
                    type="number"
                    min="0.001"
                    max={remaining}
                    step="0.001"
                    required
                    value={paymentAmount}
                    onChange={(event) => setPaymentAmount(event.target.value)}
                    placeholder={remaining.toFixed(3)}
                    disabled={!remaining || loading}
                  />
                </label>
                <label>
                  <span>{t("consultationPage.payment.method")}</span>
                  <select value={paymentMethod} onChange={(event) => setPaymentMethod(event.target.value)} disabled={loading}>
                    <option value="cash">{t("consultationPage.payment.cash")}</option>
                    <option value="card">{t("consultationPage.payment.card")}</option>
                  </select>
                </label>
                <button className="payment-btn" type="submit" disabled={!remaining || loading}>
                  {loading ? t("consultationPage.payment.saving") : t("consultationPage.payment.addPayment")}
                </button>
              </form>
              <section className="payment-history">
                <h4>{t("consultationPage.payment.paymentHistory")}</h4>
                {allPayments.length ? allPayments.map((entry) => (
                  <div className="payment-history-row" key={entry.id}>
                    <span>{entry.visitDate ? new Date(entry.visitDate).toLocaleDateString() : "—"}</span>
                    <span>{entry.method === "card" ? t("consultationPage.payment.card") : t("consultationPage.payment.cash")}</span>
                    <strong>{Number(entry.amount || 0).toFixed(3)} TND</strong>
                  </div>
                )) : <p>{t("consultationPage.payment.noPayments")}</p>}
              </section>
            </>
          )}
        </>
      )}
    </div>
  );
}